import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  ArrowRight,
  BarChart3,
  CalendarDays,
  Clock3,
  Filter,
  Flag,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  Target,
  X
} from "lucide-react";
import dataRaw from "./data/dv3-data.json";
import eventRateUpsRaw from "./data/event-rateups.json";
import formDataRaw from "./data/dragon-forms.json";
import localesRaw from "./data/locales.json";
import {
  calculateBreedingResults,
  findMultiTargetCombos,
  findParentCombos,
  findShortestPath,
  getParentWarnings
} from "./lib/breeding";
import { formatAttempts, formatDuration } from "./lib/format";
import type { BreedingOptions, BreedingResult, Dragon, GameData, MultiTargetCombo, ParentCombo } from "./types";
import "./styles.css";

const data = dataRaw as GameData;
type LocaleMeta = {
  code: string;
  englishName: string;
  nativeName: string;
  direction: "ltr" | "rtl";
  font: string;
  tier: number;
  order: number;
  termCount: number;
  coverage: number;
};
type LocalesData = {
  defaultLocale: string;
  sourcePatchVersion: string;
  locales: LocaleMeta[];
};
type I18nPayload = {
  code: string;
  englishName: string;
  nativeName: string;
  direction: "ltr" | "rtl";
  terms: Record<string, string>;
};
type I18nState = {
  locale: LocaleMeta;
  terms: Record<string, string>;
  loading: boolean;
  setLocaleCode: (code: string) => void;
};

const localesData = localesRaw as LocalesData;
const supportedLocales = localesData.locales;
const defaultLocale = supportedLocales.find((locale) => locale.code === localesData.defaultLocale) ?? supportedLocales[0];
let activeLocaleCode = defaultLocale.code;
let activeLocaleDirection: "ltr" | "rtl" = defaultLocale.direction;
let activeTerms: Record<string, string> = {};
let activeTermEntries: Array<[string, string]> = [];

type EventRateUpEntry = {
  id: number;
  startsAt: number;
  endsAt: number;
  disabled?: boolean;
  dragonNames: string[];
  uiPriority?: number;
};
type EventRateUpData = {
  generatedAt?: string;
  activeDragonNames?: string[];
  events?: EventRateUpEntry[];
  source?: {
    type?: string;
    path?: string;
    stale?: boolean;
    mtime?: string;
  };
};

const eventRateUps = eventRateUpsRaw as EventRateUpData;
const recentDragonStorageKey = "dv3.recentDragons";
type DragonFormStage = "hatch" | "hatchling" | "adult";
type DragonFormVariant = "normal" | "chroma";
type DragonFormData = Record<DragonFormVariant, Record<string, DragonFormStage[]>> & {
  chromaIcons: Record<string, DragonFormStage[]>;
};

const formData = formDataRaw as DragonFormData;
const siteOrigin = "https://dv3guide.com";
const dragons = data.dragons;
const calculatorTargetDragons = dragons;
const calculatorTargetIds = new Set(calculatorTargetDragons.map((dragon) => dragon.id));
const activeEventRateUpEvents = getActiveEventRateUpEvents(eventRateUps, Date.now());
const activeEventRateUpNames = getActiveEventRateUpNames(eventRateUps, activeEventRateUpEvents);
const activeEventRateUpDragons = dragons.filter((dragon) => activeEventRateUpNames.includes(dragon.name));
const activeEventRateUpIds = new Set(activeEventRateUpDragons.map((dragon) => dragon.id));
const initialParentA = dragons.find((dragon) => dragon.id === 1001000001)?.id ?? dragons[0].id;
const initialParentB = dragons.find((dragon) => dragon.id === 1002000001)?.id ?? dragons[1].id;
const initialTarget = calculatorTargetDragons.find((dragon) => dragon.id === 1011000004)?.id ?? calculatorTargetDragons[0]?.id ?? dragons[0].id;
const initialOwned = new Set(dragons.filter((dragon) => dragon.buyable).map((dragon) => dragon.id));
const dragonAssetCodes = new Set(Object.keys(formData.normal ?? {}));
const eggElementCodes = new Set([
  "chaos",
  "dark",
  "dawn",
  "divine",
  "dream",
  "dusk",
  "earth",
  "fire",
  "light",
  "lightning",
  "soul",
  "steel",
  "water",
  "wind"
]);

type MainMode = "target" | "parents";
type TargetTab = "list" | "multi" | "path";
type AdPlacement = "top" | "inContent" | "bottom";
type StaticPageId = "privacy" | "about" | "contact" | "faq";
type DirectorySort = "no" | "name" | "rarity";
type ParentOutcomePreview = { partner: Dragon; result: BreedingResult };
type FeaturedDragonReason = "event" | "recent" | "recommended";
type FeaturedDragonItem = { dragon: Dragon; reason: FeaturedDragonReason };
type AnalyticsWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (command: string, eventName: string, params?: Record<string, unknown>) => void;
  zaraz?: { track?: (eventName: string, params?: Record<string, unknown>) => void };
};

const formStageLabels: Record<DragonFormStage, string> = {
  hatch: "1단계",
  hatchling: "2단계",
  adult: "3단계"
};
const formStageOrder: DragonFormStage[] = ["hatch", "hatchling", "adult"];
const comboPageSize = 30;
const calculatorFaqItems = [
  {
    question: "목표로 보기는 언제 쓰나요?",
    answer:
      "원하는 드래곤을 먼저 정했을 때 사용합니다. 목표 드래곤을 선택하면 만들 수 있는 부모 조합을 성공 확률과 부화 시간 기준으로 비교할 수 있습니다."
  },
  {
    question: "부모로 보기는 언제 쓰나요?",
    answer:
      "이미 보유한 부모 드래곤 두 마리로 어떤 결과가 나오는지 확인할 때 사용합니다. 조합 결과에서 부모용 드래곤을 누르면 해당 드래곤의 교배식으로 바로 이동할 수 있습니다."
  },
  {
    question: "가챠 드래곤은 왜 목표 목록에 없나요?",
    answer:
      "목표 목록은 교배로 만들 수 있는 드래곤을 중심으로 보여줍니다. 가챠, 상점, 레이드 드래곤은 도감과 상세 페이지에서 획득처를 확인하는 방식이 더 정확합니다."
  },
  {
    question: "이벤트 확률업은 어떻게 반영되나요?",
    answer:
      "현재 적용 중인 이벤트 확률업 대상은 결과 목록에서 기본 확률과 함께 자동으로 표시됩니다. 이벤트가 없는 드래곤은 별도 표시 없이 기본 확률만 보입니다."
  },
  {
    question: "최단 경로는 어떤 기준인가요?",
    answer:
      "보유 드래곤에서 목표 드래곤까지 필요한 교배 단계를 줄이는 기준으로 계산합니다. 보유 목록을 바꾸면 가능한 경로도 다시 계산됩니다."
  }
];
const siteFaqItems = [
  ...calculatorFaqItems,
  {
    question: "조합이나 확률이 틀리면 어디에 제보하나요?",
    answer:
      "각 계산기와 도감 상세 페이지의 오류 제보 버튼을 누르거나 contact@dv3guide.com 으로 드래곤 이름, 화면 주소, 잘못된 내용을 보내 주세요."
  },
  {
    question: "도감 상세 페이지에는 어떤 정보를 보나요?",
    answer:
      "드래곤별 얻는법, 추천 교배식, 부모로 활용했을 때의 대표 결과, 속성, 부화 시간, 능력치, 성장 단계 이미지를 확인할 수 있습니다."
  },
  {
    question: "검색어나 클릭 데이터는 왜 필요한가요?",
    answer:
      "많이 찾는 드래곤과 자주 사용하는 기능을 파악해 오류 수정, 이벤트 데이터 갱신, 모바일 화면 개선 우선순위를 정하기 위해 사용합니다."
  },
  {
    question: "광고는 왜 표시되나요?",
    answer:
      "DV3 공략을 무료로 운영하기 위한 호스팅과 도메인 비용을 마련하기 위해 광고를 사용할 수 있습니다. 계산기 사용을 방해하지 않는 위치에 제한적으로 배치합니다."
  }
];

const appEnv = (import.meta as ImportMeta & { env: Record<string, string | undefined> }).env;
const adsenseClient = appEnv.VITE_ADSENSE_CLIENT?.trim() ?? "";
let lastTrackedPageView = "";
const adSlots: Record<AdPlacement, string> = {
  top: appEnv.VITE_ADSENSE_SLOT_TOP?.trim() ?? "",
  inContent: appEnv.VITE_ADSENSE_SLOT_IN_CONTENT?.trim() ?? "",
  bottom: appEnv.VITE_ADSENSE_SLOT_BOTTOM?.trim() ?? ""
};

type PageMeta = {
  title: string;
  description: string;
  canonicalPath: string;
  imagePath?: string | null;
  keywords?: string[];
  jsonLd?: unknown;
};

function getInitialLocaleCode() {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get("lang")?.trim();
  const fromStorage = window.localStorage.getItem("dv3.locale")?.trim();
  const fromBrowser = navigator.language || navigator.languages?.[0] || "";
  const candidates = [fromUrl, fromStorage, fromBrowser, fromBrowser.split("-")[0], defaultLocale.code].filter(
    (item): item is string => Boolean(item)
  );

  return candidates.find((code) => supportedLocales.some((locale) => locale.code.toLowerCase() === code.toLowerCase())) ?? defaultLocale.code;
}

function useI18n(): I18nState {
  const [localeCode, setLocaleCodeState] = useState(getInitialLocaleCode);
  const locale = supportedLocales.find((item) => item.code === localeCode) ?? defaultLocale;
  const [terms, setTerms] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  syncI18nGlobals(locale, terms);

  useEffect(() => {
    let cancelled = false;
    setLoading(locale.code !== "ko");

    const load = async () => {
      if (locale.code === "ko") {
        if (!cancelled) setTerms({});
        return;
      }

      try {
        const response = await fetch(`/i18n/${encodeURIComponent(locale.code)}.json`);
        if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
        const payload = (await response.json()) as I18nPayload;
        if (!cancelled) setTerms(payload.terms ?? {});
      } catch (error) {
        console.error(`Failed to load i18n locale ${locale.code}`, error);
        if (!cancelled) setTerms({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [locale.code]);

  useEffect(() => {
    document.documentElement.lang = locale.code;
    document.documentElement.dir = locale.direction;
    document.documentElement.dataset.locale = locale.code;
    window.localStorage.setItem("dv3.locale", locale.code);
  }, [locale, terms]);

  const setLocaleCode = (code: string) => {
    const next = supportedLocales.find((item) => item.code === code);
    if (!next) return;
    const url = new URL(window.location.href);
    if (next.code === defaultLocale.code) url.searchParams.delete("lang");
    else url.searchParams.set("lang", next.code);
    window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
    setLocaleCodeState(next.code);
  };

  return { locale, terms, loading, setLocaleCode };
}

function syncI18nGlobals(locale: LocaleMeta, terms: Record<string, string>) {
  activeLocaleCode = locale.code;
  activeLocaleDirection = locale.direction;
  activeTerms = terms;
  activeTermEntries = Object.entries(terms).sort((a, b) => b[0].length - a[0].length);
}

function useDomTranslation(i18n: I18nState) {
  useEffect(() => {
    if (i18n.locale.code === "ko") return;
    translateDocument();
  });
}

function translateDocument() {
  const root = document.getElementById("root");
  if (!root || activeTermEntries.length === 0) return;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    const parent = node.parentElement;
    if (!parent || ["SCRIPT", "STYLE", "NOSCRIPT"].includes(parent.tagName)) continue;
    nodes.push(node);
  }

  for (const node of nodes) {
    const translated = translateText(node.nodeValue ?? "");
    if (translated !== node.nodeValue) node.nodeValue = translated;
  }

  for (const element of root.querySelectorAll<HTMLElement>("[placeholder], [title], [aria-label]")) {
    for (const attr of ["placeholder", "title", "aria-label"]) {
      const value = element.getAttribute(attr);
      if (!value) continue;
      const translated = translateText(value);
      if (translated !== value) element.setAttribute(attr, translated);
    }
  }
}

function translateText(value: string) {
  if (!value || activeTermEntries.length === 0) return value;
  let output = value;
  for (const [source, translated] of activeTermEntries) {
    if (!source || source.length < 2 || !output.includes(source)) continue;
    output = output.split(source).join(translated);
  }
  return output;
}

function localizeForSearch(value: string) {
  const translated = translateText(value);
  return translated === value ? value : `${value}${translated}`;
}

const staticPageByPath: Record<string, StaticPageId> = {
  "/privacy": "privacy",
  "/about": "about",
  "/contact": "contact",
  "/faq": "faq"
};

const dropFilterOptions = [
  { value: "all", label: "전체" },
  { value: "breed_normal", label: "교배" },
  { value: "gacha_normal", label: "가챠" },
  { value: "gacha_pickup", label: "픽업" },
  { value: "raid", label: "레이드" },
  { value: "sell", label: "판매" }
];

const elementFilterOptions = Object.values(data.elements)
  .filter((element) => dragons.some((dragon) => dragon.elementCodes.includes(element.code)))
  .sort((a, b) => a.requireUserLevel - b.requireUserLevel || compareLocalized(a.name, b.name));

function hasBreedingRecipe(dragon: Dragon) {
  return dragon.drop === "breed_normal" && (dragon.breeding.portion > 0 || dragon.breeding.fixedProportion > 0);
}

function getActiveEventRateUpEvents(rateUps: EventRateUpData, now: number) {
  return (rateUps.events ?? []).filter((event) => !event.disabled && event.startsAt <= now && now <= event.endsAt);
}

function getActiveEventRateUpNames(rateUps: EventRateUpData, activeEvents: EventRateUpEntry[]) {
  if (rateUps.events !== undefined) return [...new Set(activeEvents.flatMap((event) => event.dragonNames))];
  return rateUps.activeDragonNames ?? [];
}

function formatEventRateUpPeriod(events: EventRateUpEntry[], localeCode = activeLocaleCode) {
  if (events.length === 0) return "";
  const endsAt = Math.min(...events.map((event) => event.endsAt));
  try {
    return new Intl.DateTimeFormat(localeCode, {
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: false,
      timeZone: "Asia/Seoul"
    }).format(endsAt);
  } catch {
    return new Intl.DateTimeFormat("ko-KR", {
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: false,
      timeZone: "Asia/Seoul"
    }).format(endsAt);
  }
}

function formatNumber(value: number) {
  try {
    return value.toLocaleString(activeLocaleCode);
  } catch {
    return value.toLocaleString("ko-KR");
  }
}

function formatCountLabel(value: number, unit: "items" | "combos" | "species" | "candidates" | "owned") {
  const number = formatNumber(value);
  if (activeLocaleCode === "ko") {
    if (unit === "combos") return `${number}개 조합`;
    if (unit === "species") return `${number}종`;
    if (unit === "candidates") return `${number}종 후보`;
    if (unit === "owned") return `${number}종 보유`;
    return `${number}개`;
  }
  if (activeLocaleCode === "ja") {
    if (unit === "combos") return `${number}件の組み合わせ`;
    if (unit === "species") return `${number}種`;
    if (unit === "candidates") return `${number}種の候補`;
    if (unit === "owned") return `${number}種所持`;
    return `${number}件`;
  }
  if (unit === "combos") return `${number} ${translateText("combos")}`;
  if (unit === "species") return `${number} ${translateText("species")}`;
  if (unit === "candidates") return `${number} ${translateText("candidates")}`;
  if (unit === "owned") return `${number} ${translateText("owned")}`;
  return `${number} ${translateText("items")}`;
}

function formatOutOfCount(value: number) {
  const number = formatNumber(value);
  if (activeLocaleCode === "ko") return `${number}개 중`;
  if (activeLocaleCode === "ja") return `${number}件中`;
  return `${number} ${translateText("total")}`;
}

function formatShownCount(visible: number, total: number) {
  if (activeLocaleCode === "ko") return `${formatNumber(total)}개 중 ${formatNumber(visible)}개 표시`;
  if (activeLocaleCode === "ja") return `${formatNumber(total)}件中 ${formatNumber(visible)}件表示`;
  return `${formatNumber(visible)} / ${formatNumber(total)} ${translateText("shown")}`;
}

function formatMoreCount(value: number) {
  if (activeLocaleCode === "ko") return `더 보기 ${formatNumber(value)}개`;
  if (activeLocaleCode === "ja") return `さらに${formatNumber(value)}件表示`;
  return `${translateText("Show more")} ${formatNumber(value)}`;
}

function formatDateTime(value?: string | number, localeCode = activeLocaleCode) {
  if (!value) return "알 수 없음";
  const date = typeof value === "number" ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return "알 수 없음";
  try {
    return new Intl.DateTimeFormat(localeCode, {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: false,
      timeZone: "Asia/Seoul"
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat("ko-KR", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: false,
      timeZone: "Asia/Seoul"
    }).format(date);
  }
}

function formatEventDday(event: EventRateUpEntry) {
  const remainingMs = event.endsAt - Date.now();
  if (remainingMs <= 0) return "종료";
  const days = Math.floor(remainingMs / 86_400_000);
  const hours = Math.ceil((remainingMs % 86_400_000) / 3_600_000);
  if (days > 0) return `${days}일 ${hours}시간 남음`;
  return `${Math.max(1, hours)}시간 남음`;
}

function readRecentDragonIds() {
  try {
    const value = window.localStorage.getItem(recentDragonStorageKey);
    if (!value) return [];
    return (JSON.parse(value) as unknown[])
      .map((item) => Number(item))
      .filter((item) => Number.isFinite(item));
  } catch {
    return [];
  }
}

function rememberDragonInterest(dragon: Dragon) {
  try {
    const next = [dragon.id, ...readRecentDragonIds().filter((id) => id !== dragon.id)].slice(0, 12);
    window.localStorage.setItem(recentDragonStorageKey, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent("dv3:recent-dragons", { detail: next }));
  } catch {
    // Local storage may be blocked; analytics events still carry the click.
  }
}

function useRecentDragonIds() {
  const [ids, setIds] = useState<number[]>(() => readRecentDragonIds());
  useEffect(() => {
    const sync = () => setIds(readRecentDragonIds());
    window.addEventListener("storage", sync);
    window.addEventListener("dv3:recent-dragons", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("dv3:recent-dragons", sync);
    };
  }, []);
  return ids;
}

function App() {
  useAdSenseScript();
  const i18n = useI18n();
  useDomTranslation(i18n);
  const pathname = window.location.pathname;
  if (pathname === "/events" || pathname === "/rateup") return <EventRateUpPage i18n={i18n} />;
  if (pathname === "/dragons") return <DragonDirectoryPage i18n={i18n} />;
  if (pathname.startsWith("/dragons/")) {
    const dragon = getDragonFromPath(pathname);
    return dragon ? <DragonDetailPage dragon={dragon} i18n={i18n} /> : <DragonNotFoundPage i18n={i18n} />;
  }

  const staticPage = staticPageByPath[pathname];
  if (staticPage) return <StaticPage page={staticPage} i18n={i18n} />;

  const defaultTargetId = getTargetIdFromUrl() ?? initialTarget;
  const dragonById = useMemo(() => new Map(dragons.map((dragon) => [dragon.id, dragon])), []);
  const parentChoices = useMemo(() => dragons.filter((dragon) => dragon.canBreedAsParent), []);
  const [mode, setMode] = useState<MainMode>("target");
  const [parentAId, setParentAId] = useState(initialParentA);
  const [parentBId, setParentBId] = useState(initialParentB);
  const [targetId, setTargetId] = useState(defaultTargetId);
  const [userLevel, setUserLevel] = useState(99);
  const [query, setQuery] = useState("");
  const [dropFilter, setDropFilter] = useState("all");
  const [minChance, setMinChance] = useState(0);
  const [ownedDids, setOwnedDids] = useState(initialOwned);
  const [tab, setTab] = useState<TargetTab>("list");
  const [multiTargetDids, setMultiTargetDids] = useState<number[]>([defaultTargetId]);
  const [multiTargetAddId, setMultiTargetAddId] = useState(defaultTargetId);
  const recentDragonIds = useRecentDragonIds();
  const i18nVersion = `${i18n.locale.code}:${Object.keys(i18n.terms).length}`;
  const activeEventRateUpPeriodText = useMemo(
    () => formatEventRateUpPeriod(activeEventRateUpEvents, i18n.locale.code),
    [i18n.locale.code, i18nVersion]
  );
  const featuredDragonItems = useMemo(() => getFeaturedDragonItems(recentDragonIds), [recentDragonIds]);

  const parentA = dragonById.get(parentAId) ?? dragons[0];
  const parentB = dragonById.get(parentBId) ?? dragons[1];
  const target = dragonById.get(targetId) ?? calculatorTargetDragons[0] ?? dragons[0];
  const targetChoices = useMemo(
    () => (calculatorTargetIds.has(target.id) ? calculatorTargetDragons : [target, ...calculatorTargetDragons]),
    [target]
  );
  const options: BreedingOptions = useMemo(
    () => ({ userLevel, allowSameSpecies: false, activeRateUpDids: activeEventRateUpIds }),
    [userLevel]
  );

  usePageMeta({
    title: mode === "target" ? `${target.name} 교배 계산기 - DV3 공략` : "드래곤빌리지3 교배 계산기 - DV3 공략",
    description:
      mode === "target"
        ? `${target.name}을 얻기 위한 드래곤빌리지3 부모 조합, 성공 확률, 부화 시간, 기대 횟수, 최단 루트를 확인하세요.`
        : `${parentA.name}와 ${parentB.name} 조합에서 나올 수 있는 드래곤, 성공 확률, 부화 시간을 확인하세요.`,
    canonicalPath: mode === "target" ? getCalculatorTargetPath(target) : "/",
    imagePath: "/assets/dv3-tool-preview.png",
    keywords:
      mode === "target"
        ? ["드래곤빌리지3", "DV3 공략", `${target.name} 교배식`, `${target.name} 교배`, `${target.name} 얻는법`, "교배 계산기"]
        : ["드래곤빌리지3", "DV3 공략", `${parentA.name} ${parentB.name} 조합`, "교배 결과", "교배 계산기"],
    jsonLd: buildCalculatorJsonLd(target)
  });

  useEffect(() => {
    if (window.location.pathname !== "/") return;
    const nextPath = mode === "target" ? getCalculatorTargetPath(target) : "/";
    const currentPath = `${window.location.pathname}${window.location.search}`;
    if (currentPath !== nextPath) {
      window.history.replaceState(null, "", nextPath);
    }
  }, [mode, target.id]);

  const pairResults = useMemo(
    () => calculateBreedingResults(dragons, parentA, parentB, data.tierFixedRateMax, options),
    [parentA, parentB, options]
  );

  const filteredPairResults = useMemo(
    () => filterBreedingResults(pairResults, query, dropFilter, minChance),
    [pairResults, query, dropFilter, minChance, i18nVersion]
  );
  const fastestPairResult = useMemo(
    () => filteredPairResults.reduce<BreedingResult | undefined>(
      (fastest, result) => (!fastest || result.hatchingSeconds < fastest.hatchingSeconds ? result : fastest),
      undefined
    ),
    [filteredPairResults]
  );

  const parentWarnings = useMemo(() => getParentWarnings(parentA, parentB, options), [parentA, parentB, options]);

  const targetCombos = useMemo(
    () => findParentCombos(dragons, target.id, data.tierFixedRateMax, options),
    [target.id, options]
  );

  const filteredTargetCombos = useMemo(
    () => filterParentCombos(targetCombos, query, minChance),
    [targetCombos, query, minChance, i18nVersion]
  );

  const selectedMultiTargets = useMemo(
    () =>
      multiTargetDids
        .map((did) => dragonById.get(did))
        .filter((dragon): dragon is Dragon => Boolean(dragon)),
    [multiTargetDids, dragonById]
  );

  const multiTargetCombos = useMemo(
    () => (tab === "multi" ? findMultiTargetCombos(dragons, multiTargetDids, data.tierFixedRateMax, options) : []),
    [tab, multiTargetDids, options]
  );

  const filteredMultiTargetCombos = useMemo(
    () => filterMultiTargetCombos(multiTargetCombos, query, minChance),
    [multiTargetCombos, query, minChance, i18nVersion]
  );

  const pathPlan = useMemo(
    () => findShortestPath(dragons, ownedDids, target.id, data.tierFixedRateMax, options),
    [ownedDids, target.id, options]
  );

  const ownedDragons = useMemo(
    () =>
      [...ownedDids]
        .map((did) => dragonById.get(did))
        .filter((dragon): dragon is Dragon => Boolean(dragon))
        .sort((a, b) => a.id - b.id),
    [ownedDids, dragonById]
  );

  const resetAll = () => {
    setMode("target");
    setParentAId(initialParentA);
    setParentBId(initialParentB);
    setTargetId(initialTarget);
    setMultiTargetDids([initialTarget]);
    setMultiTargetAddId(initialTarget);
    setUserLevel(99);
    setQuery("");
    setDropFilter("all");
    setMinChance(0);
  };

  const changeMode = (nextMode: MainMode) => {
    setMode(nextMode);
    setTab("list");
    trackSiteEvent("change_mode", { mode: nextMode });
  };

  const changeTarget = (did: number) => {
    const nextTarget = dragonById.get(did);
    setTargetId(did);
    setMultiTargetAddId(did);
    setMultiTargetDids((current) => (current.length <= 1 ? [did] : current));
    if (nextTarget) {
      rememberDragonInterest(nextTarget);
      trackSiteEvent("select_target", {
        dragon: nextTarget.name,
        code: nextTarget.code,
        source: "calculator"
      });
    }
  };

  const addMultiTarget = (did: number) => {
    if (!calculatorTargetIds.has(did)) return;
    setMultiTargetDids((current) => (current.includes(did) ? current : [...current, did]));
    const addedTarget = dragonById.get(did);
    if (addedTarget) {
      rememberDragonInterest(addedTarget);
      trackSiteEvent("add_multi_target", {
        dragon: addedTarget.name,
        code: addedTarget.code
      });
    }
  };

  const removeMultiTarget = (did: number) => {
    setMultiTargetDids((current) => {
      const next = current.filter((item) => item !== did);
      return next.length > 0 ? next : [targetId];
    });
  };

  const selectRecipeTarget = (dragon: Dragon) => {
    setTargetId(dragon.id);
    setMode("target");
    setTab("list");
    setQuery("");
    setDropFilter("all");
    setMinChance(0);
    if (calculatorTargetIds.has(dragon.id)) {
      setMultiTargetAddId(dragon.id);
      setMultiTargetDids((current) => (current.length <= 1 ? [dragon.id] : current));
    }
    rememberDragonInterest(dragon);
    trackSiteEvent("open_recipe", {
      dragon: dragon.name,
      code: dragon.code
    });
  };

  return (
    <main className="dv-app" key={i18n.locale.code}>
      <h1 style={visuallyHiddenStyle}>드래곤빌리지3 교배 계산기</h1>
      <header className="dv-head">
        <a className="dv-logo dv-logo-link" href="/">
          <span>드래곤빌리지3</span>
          <strong>DV3 공략</strong>
        </a>
        <div className="dv-nav-pills" aria-label="주요 기능">
          <span>역교배</span>
          <span>조합 결과</span>
          <span>최단 루트</span>
          <a href="/events">이벤트</a>
          <a href="/dragons">도감</a>
        </div>
        <LanguageSelect i18n={i18n} />
      </header>

      <section className={`dv-hero dv-hero-${mode}`}>
        <div className="dv-mode" role="tablist" aria-label="계산 방식">
          <button className={mode === "target" ? "active" : ""} onClick={() => changeMode("target")} type="button">
            <Target size={16} />목표로 보기
          </button>
          <button className={mode === "parents" ? "active" : ""} onClick={() => changeMode("parents")} type="button">
            <BarChart3 size={16} />부모로 보기
          </button>
        </div>

        <div className="dv-picks">
          {mode === "target" ? (
            <SelectedDragonControl eyebrow="목표 드래곤" title="" dragon={target} value={targetId} dragons={targetChoices} onChange={changeTarget} />
          ) : (
            <div className="dv-parent-pickers">
              <SelectedDragonControl eyebrow="부모 1" title="" dragon={parentA} value={parentAId} dragons={parentChoices} onChange={setParentAId} />
              <SelectedDragonControl eyebrow="부모 2" title="" dragon={parentB} value={parentBId} dragons={parentChoices} onChange={setParentBId} />
            </div>
          )}
        </div>

        <HeroAnswer
          mode={mode}
          target={target}
          bestCombo={filteredTargetCombos[0]}
          parentA={parentA}
          parentB={parentB}
          bestResults={filteredPairResults.slice(0, 3)}
          fastestResult={fastestPairResult}
          comboCount={targetCombos.length}
          resultCount={pairResults.length}
          warnings={parentWarnings}
          onRecipeTargetSelect={selectRecipeTarget}
        />
      </section>

      <FeaturedDragonStrip items={featuredDragonItems} onSelect={changeTarget} />
      <DataTrustBar compact />

      <AdSlot placement="top" />

      <section className="dv-detail">
        {mode === "target" && (
          <nav className="dv-tabs" role="tablist">
            <button className={tab === "list" ? "active" : ""} onClick={() => setTab("list")} type="button">
              조합 목록<em>{filteredTargetCombos.length}</em>
            </button>
            <button className={tab === "multi" ? "active" : ""} onClick={() => setTab("multi")} type="button">
              동시 목표<em>{selectedMultiTargets.length}</em>
            </button>
            <button className={tab === "path" ? "active" : ""} onClick={() => setTab("path")} type="button">최단 경로</button>
          </nav>
        )}

        <div className="dv-panel">
          {(mode === "parents" || tab === "list" || tab === "multi") && (
            <>
              <p className="dv-hint">
                {mode === "parents" ? (
                  <>
                    선택한 <b>부모 둘</b>에서 나올 수 있는 드래곤이에요. 각 줄은 <b>결과</b> · <b>성공 확률</b> · <b>부화 시간</b> · <b>기대 횟수</b>(평균 몇 번 교배하면 나오는지) 순서.
                  </>
                ) : tab === "multi" ? (
                  <>
                    여러 <b>목표</b> 드래곤을 한 번의 교배에서 모두 노릴 수 있는 부모 조합이에요. 선택한 목표가 하나라도 빠지면 목록에 나오지 않습니다.
                  </>
                ) : (
                  <>
                    선택한 <b>목표</b> 드래곤을 만들 수 있는 부모 조합이에요. 각 줄은 <b>부모 + 부모 → 결과 · 성공 확률 · 부화 시간 · 기대 횟수</b> 순서.
                  </>
                )}
              </p>
              {activeEventRateUpDragons.length > 0 && (
                <p className="dv-event-note">
                  <Sparkles size={15} />
                  이벤트 확률업 · <b>{activeEventRateUpDragons.map((dragon) => dragon.name).join(", ")}</b>
                  {activeEventRateUpPeriodText && <span> {activeEventRateUpPeriodText}까지</span>} 결과는 기본 확률과 함께 자동 표시됩니다.
                </p>
              )}
              <div className="dv-filters">
                <label className="dv-field dv-search-field">
                  <span>검색</span>
                  <div className="dv-input-with-icon">
                    <Search size={15} />
                    <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="이름, 코드, 속성" />
                  </div>
                </label>
                <button className="dv-reset-button" onClick={resetAll} type="button">
                  <RefreshCw size={15} />초기화
                </button>
                <details className="dv-advanced-filters">
                  <summary>상세 필터</summary>
                  <div>
                    <label className="dv-field">
                      <span>테이머 레벨</span>
                      <input type="number" min={1} max={99} value={userLevel} onChange={(event) => setUserLevel(Number(event.target.value))} />
                    </label>
                    <label className="dv-field">
                      <span>최소 확률</span>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={0.1}
                        value={minChance}
                        onChange={(event) => setMinChance(Number(event.target.value))}
                      />
                    </label>
                    <label className="dv-field">
                      <span>획득처</span>
                      <select value={dropFilter} onChange={(event) => setDropFilter(event.target.value)}>
                        <option value="all">전체</option>
                        <option value="breed_normal">교배</option>
                        <option value="gacha_normal">가챠</option>
                        <option value="gacha_pickup">픽업</option>
                        <option value="raid">레이드</option>
                        <option value="sell">판매</option>
                      </select>
                    </label>
                  </div>
                </details>
              </div>
              <AdSlot placement="inContent" />
              {mode === "parents" ? (
                <ParentResultBoard
                  parentA={parentA}
                  parentB={parentB}
                  results={filteredPairResults}
                  allResultCount={pairResults.length}
                  warnings={parentWarnings}
                  onRecipeTargetSelect={selectRecipeTarget}
                />
              ) : tab === "multi" ? (
                <MultiTargetBoard
                  selectedTargets={selectedMultiTargets}
                  addTargetId={multiTargetAddId}
                  addTargetOptions={calculatorTargetDragons}
                  combos={filteredMultiTargetCombos}
                  allComboCount={multiTargetCombos.length}
                  onAddTargetIdChange={setMultiTargetAddId}
                  onAddTarget={() => addMultiTarget(multiTargetAddId)}
                  onRemoveTarget={removeMultiTarget}
                  onRecipeTargetSelect={selectRecipeTarget}
                />
              ) : (
                <TargetRecipeBoard target={target} combos={filteredTargetCombos} allComboCount={targetCombos.length} onParentRecipeSelect={selectRecipeTarget} />
              )}
            </>
          )}
          {mode === "target" && tab === "path" && (
            <>
              <p className="dv-hint">
                내가 <b>가진 드래곤</b>에서 <b>목표</b>까지 단계별로 교배하는 최단 루트예요. 아래에서 보유 드래곤을 추가/제거하면 경로가 다시 계산돼요.
              </p>
              <PathPanel
                target={target}
                ownedDragons={ownedDragons}
                ownedDids={ownedDids}
                setOwnedDids={setOwnedDids}
                pathPlan={pathPlan}
                onRecipeTargetSelect={selectRecipeTarget}
              />
            </>
          )}
        </div>
      </section>

      <CalculatorGuideSection />
      <AdSlot placement="bottom" />
      <SiteFooter />
    </main>
  );
}

const visuallyHiddenStyle: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0
};

function useAdSenseScript() {
  useEffect(() => {
    if (!adsenseClient || document.querySelector('script[src*="pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"]')) return;
    const script = document.createElement("script");
    script.async = true;
    script.crossOrigin = "anonymous";
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(adsenseClient)}`;
    document.head.appendChild(script);
  }, []);
}

function usePageMeta({ title, description, canonicalPath, imagePath, keywords, jsonLd }: PageMeta) {
  const i18nSignature = `${activeLocaleCode}:${activeTermEntries.length}`;
  useEffect(() => {
    const canonicalUrl = absoluteUrl(canonicalPath);
    const imageUrl = imagePath ? absoluteUrl(imagePath) : absoluteUrl("/assets/dv3-tool-preview.png");
    const localizedTitle = translateText(title);
    const localizedDescription = translateText(description);
    const localizedJsonLd = localizeJsonLd(withBaseJsonLd(jsonLd));

    document.title = localizedTitle;
    upsertMeta("name", "description", localizedDescription);
    upsertMeta(
      "name",
      "keywords",
      (keywords?.length ? keywords : ["드래곤빌리지3", "DV3 공략", "교배 계산기", "드래곤 도감"]).map(translateText).join(", ")
    );
    upsertMeta("property", "og:title", localizedTitle);
    upsertMeta("property", "og:description", localizedDescription);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", imageUrl);
    upsertMeta("property", "og:site_name", translateText("DV3 공략"));
    upsertMeta("property", "og:type", "website");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", localizedTitle);
    upsertMeta("name", "twitter:description", localizedDescription);
    upsertMeta("name", "twitter:image", imageUrl);

    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    const scriptId = "dv-page-jsonld";
    const previousScript = document.getElementById(scriptId) as HTMLScriptElement | null;
    const script = previousScript ?? document.createElement("script");
    script.id = scriptId;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(localizedJsonLd);
    if (!previousScript) document.head.appendChild(script);

    const pageViewKey = `${canonicalPath}:${activeLocaleCode}`;
    if (lastTrackedPageView !== pageViewKey) {
      lastTrackedPageView = pageViewKey;
      trackSiteEvent("page_view", {
        path: canonicalPath,
        title: localizedTitle,
        locale: activeLocaleCode
      });
    }
  }, [title, description, canonicalPath, imagePath, keywords, jsonLd, i18nSignature]);
}

function withBaseJsonLd(jsonLd: unknown) {
  const entries = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  return [buildSiteJsonLd(), ...entries];
}

function localizeJsonLd(value: unknown): unknown {
  if (typeof value === "string") return translateText(value);
  if (Array.isArray(value)) return value.map(localizeJsonLd);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, localizeJsonLd(item)]));
}

function buildSiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${siteOrigin}/#website`,
    name: "DV3 공략",
    alternateName: ["드래곤빌리지3 공략", "드래곤빌리지3 교배 계산기"],
    url: siteOrigin,
    inLanguage: "ko-KR",
    publisher: {
      "@type": "Organization",
      name: "DV3 공략",
      url: siteOrigin
    },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${siteOrigin}/dragons?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };
}

function upsertMeta(attribute: "name" | "property", key: string, content: string) {
  let meta = document.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute(attribute, key);
    document.head.appendChild(meta);
  }
  meta.content = content;
}

function absoluteUrl(path: string) {
  if (/^https?:\/\//.test(path)) return path;
  return `${siteOrigin}${path.startsWith("/") ? path : `/${path}`}`;
}

function trackSiteEvent(eventName: string, params: Record<string, unknown> = {}) {
  const payload = {
    ...params,
    site: "dv3guide"
  };
  const win = window as AnalyticsWindow;
  win.dataLayer = win.dataLayer ?? [];
  win.dataLayer.push({ event: `dv3_${eventName}`, ...payload });
  win.gtag?.("event", eventName, payload);
  win.zaraz?.track?.(eventName, payload);
  window.dispatchEvent(new CustomEvent("dv3:analytics", { detail: { eventName, payload } }));
}

function AdSlot({ placement }: { placement: AdPlacement }) {
  const slot = adSlots[placement];

  useEffect(() => {
    if (!adsenseClient || !slot) return;
    const win = window as Window & { adsbygoogle?: unknown[] };
    win.adsbygoogle = win.adsbygoogle || [];
    try {
      win.adsbygoogle.push({});
    } catch {
      // AdSense can reject duplicate pushes during hot reload; ignore and let the next render settle.
    }
  }, [slot]);

  if (!adsenseClient || !slot) return null;

  return (
    <aside className={`dv-ad-slot dv-ad-${placement}`} aria-label="광고">
      <span>광고</span>
      <ins
        className="adsbygoogle"
        style={{ display: "block" }}
        data-ad-client={adsenseClient}
        data-ad-slot={slot}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </aside>
  );
}

function CalculatorGuideSection() {
  return (
    <section className="dv-guide-content" aria-labelledby="dv-guide-title">
      <div className="dv-guide-intro">
        <p className="eyebrow">교배 계산기 사용법</p>
        <h2 id="dv-guide-title">목표 드래곤부터 고르고, 필요한 조합만 좁혀 보기</h2>
        <p>
          DV3 공략의 교배 계산기는 드래곤빌리지3에서 원하는 드래곤을 만들기 위한 부모 조합, 부모 두 마리로 나올 수 있는 결과,
          보유 드래곤 기준 최단 경로를 한 화면에서 확인하도록 구성했습니다.
        </p>
      </div>

      <ol className="dv-guide-steps">
        <li>
          <strong>목표로 보기</strong>
          <span>원하는 드래곤을 선택하고 성공 확률, 부화 시간, 기대 횟수로 부모 조합을 비교합니다.</span>
        </li>
        <li>
          <strong>부모로 보기</strong>
          <span>가지고 있는 부모 두 마리에서 어떤 드래곤이 나오는지 확인합니다.</span>
        </li>
        <li>
          <strong>최단 경로</strong>
          <span>보유 드래곤을 기준으로 목표까지 필요한 교배 단계를 줄여 봅니다.</span>
        </li>
      </ol>

      <div className="dv-guide-cta">
        <div>
          <strong>목표, 부모, 이벤트 확률업이 헷갈리나요?</strong>
          <span>자주 묻는 질문은 별도 페이지에서 정리했습니다.</span>
        </div>
        <div className="dv-guide-actions">
          <a href="/faq">FAQ 보기</a>
          <IssueReportLink context="계산기 데이터" compact />
        </div>
      </div>
    </section>
  );
}

function DataTrustBar({ compact = false }: { compact?: boolean }) {
  const patchVersion = String(data.source?.patchVersion ?? "알 수 없음");
  const eventState = eventRateUps.source?.stale ? "확인 필요" : "확인됨";

  return (
    <section className={`dv-data-trust ${compact ? "is-compact" : ""}`} aria-label="데이터 기준">
      <div>
        <ShieldCheck size={15} />
        <span>패치</span>
        <strong>{patchVersion}</strong>
      </div>
      <div>
        <CalendarDays size={15} />
        <span>도감 갱신</span>
        <strong>{formatDateTime(data.generatedAt)}</strong>
      </div>
      <div>
        <Sparkles size={15} />
        <span>이벤트</span>
        <strong>{eventState} · {formatDateTime(eventRateUps.generatedAt)}</strong>
      </div>
    </section>
  );
}

function FeaturedDragonStrip({
  items,
  onSelect
}: {
  items: FeaturedDragonItem[];
  onSelect: (did: number) => void;
}) {
  if (items.length === 0) return null;

  return (
    <section className="dv-featured-strip" aria-label="추천 목표 드래곤">
      <div className="dv-featured-head">
        <div>
          <p className="eyebrow">바로 계산</p>
          <h2>지금 볼 만한 목표</h2>
        </div>
        <a href="/events">이벤트 보기</a>
      </div>
      <div className="dv-featured-track">
        {items.map((item) => (
          <button
            className={`dv-featured-card is-${item.reason}`}
            key={`${item.reason}-${item.dragon.id}`}
            onClick={() => {
              rememberDragonInterest(item.dragon);
              onSelect(item.dragon.id);
            }}
            type="button"
          >
            <span className="dv-featured-art">
              <DragonEgg dragon={item.dragon} size="sm" />
              <DragonArt dragon={item.dragon} size="sm" />
            </span>
            <b>{item.dragon.name}</b>
            <em>{getFeaturedReasonLabel(item.reason)}</em>
          </button>
        ))}
      </div>
    </section>
  );
}

function EventRateUpPage({ i18n }: { i18n: I18nState }) {
  const eventOptions: BreedingOptions = useMemo(
    () => ({ userLevel: 99, allowSameSpecies: false, activeRateUpDids: activeEventRateUpIds }),
    []
  );
  const activeCards = useMemo(
    () =>
      activeEventRateUpDragons.map((dragon) => {
        const combos = findParentCombos(dragons, dragon.id, data.tierFixedRateMax, eventOptions);
        return {
          dragon,
          combos,
          best: combos[0]
        };
      }),
    [eventOptions]
  );
  const eventPeriodText = formatEventRateUpPeriod(activeEventRateUpEvents, i18n.locale.code);
  const eventNames = activeEventRateUpDragons.map((dragon) => dragon.name);

  usePageMeta({
    title: "드래곤빌리지3 이벤트 확률업 - DV3 공략",
    description:
      eventNames.length > 0
        ? `현재 드래곤빌리지3 교배 확률업 대상 ${eventNames.join(", ")}의 추천 조합, 이벤트 확률, 기본 확률, 종료 시간을 확인하세요.`
        : "드래곤빌리지3 교배 확률업 이벤트 대상, 기간, 추천 조합을 확인하는 DV3 공략 이벤트 페이지입니다.",
    canonicalPath: "/events",
    imagePath: "/assets/dv3-tool-preview.png",
    keywords: ["드래곤빌리지3 이벤트", "DV3 확률업", "교배 확률업", "드래곤빌리지3 교배 이벤트", ...eventNames],
    jsonLd: buildEventsJsonLd(activeEventRateUpEvents)
  });

  useEffect(() => {
    trackSiteEvent("view_events", {
      activeCount: activeEventRateUpDragons.length,
      activeDragons: eventNames.join(",")
    });
  }, [eventNames.join(",")]);

  return (
    <main className="dv-app dv-event-app" key={i18n.locale.code}>
      <header className="dv-head">
        <a className="dv-logo dv-logo-link" href="/">
          <span>드래곤빌리지3</span>
          <strong>DV3 공략</strong>
        </a>
        <div className="dv-nav-pills" aria-label="주요 기능">
          <a href="/">교배 계산기</a>
          <span>이벤트</span>
          <a href="/dragons">도감</a>
        </div>
        <LanguageSelect i18n={i18n} />
      </header>

      <section className="event-hero">
        <div>
          <p className="eyebrow">이벤트 확률업</p>
          <h1>현재 교배 확률업</h1>
          <p>
            게임 데이터에서 확인한 확률업 대상을 기준으로 추천 부모 조합과 이벤트 적용 확률을 보여줍니다.
            결과가 바뀌면 기본 확률도 함께 표시됩니다.
          </p>
        </div>
        <strong>{activeEventRateUpDragons.length > 0 ? `${activeEventRateUpDragons.length}종 적용` : "대상 없음"}</strong>
      </section>

      <DataTrustBar />

      {activeEventRateUpDragons.length > 0 && (
        <p className="dv-event-note event-page-note">
          <Sparkles size={15} />
          이벤트 확률업 · <b>{eventNames.join(", ")}</b>
          {eventPeriodText && <span> {eventPeriodText}까지</span>}
        </p>
      )}

      <section className="event-grid" aria-label="현재 확률업 드래곤">
        {activeCards.length > 0 ? (
          activeCards.map(({ dragon, combos, best }) => (
            <article className={`event-dragon-card dragon-card-${getPrimaryElementCode(dragon)}`} key={dragon.id}>
              <div className="event-card-head">
                <div className="event-card-art">
                  <DragonEgg dragon={dragon} size="md" />
                  <DragonArt dragon={dragon} size="md" />
                </div>
                <div className="event-card-title">
                  <span>확률업 대상</span>
                  <h2>{dragon.name}</h2>
                  <ElementBadgeList dragon={dragon} />
                </div>
              </div>
              <dl className="event-card-meta">
                <div>
                  <dt>조합</dt>
                  <dd>{formatCountLabel(combos.length, "combos")}</dd>
                </div>
                <div>
                  <dt>부화</dt>
                  <dd>{formatDuration(dragon.hatchingSeconds)}</dd>
                </div>
                <div>
                  <dt>획득처</dt>
                  <dd>{getDropLabel(dragon.drop)}</dd>
                </div>
              </dl>
              {best ? (
                <div className="event-best-route">
                  <span>추천 조합</span>
                  <div className="event-route-line">
                    <DragonMini dragon={best.parentA} />
                    <Plus size={15} />
                    <DragonMini dragon={best.parentB} />
                    <ArrowRight size={16} />
                    <DragonMini dragon={dragon} />
                  </div>
                  <div className="event-best-stats">
                    <ChanceValue result={best.target} />
                    <strong>기대 {formatAttempts(best.target.chance)}</strong>
                    <strong>{formatDuration(best.expectedSeconds)}</strong>
                  </div>
                </div>
              ) : (
                <p className="dv-empty">현재 조건에서 교배 조합을 찾지 못했습니다.</p>
              )}
              <div className="event-card-actions">
                <a
                  className="icon-button"
                  href={getCalculatorTargetPath(dragon)}
                  onClick={() => {
                    rememberDragonInterest(dragon);
                    trackSiteEvent("select_target", { dragon: dragon.name, code: dragon.code, source: "event_page" });
                  }}
                >
                  <Target size={15} />
                  교배식 보기
                </a>
                <a className="icon-button" href={getDragonDetailPath(dragon)} onClick={() => rememberDragonInterest(dragon)}>
                  도감
                </a>
              </div>
            </article>
          ))
        ) : (
          <p className="empty">현재 활성화된 교배 확률업 이벤트가 없습니다.</p>
        )}
      </section>

      <section className="event-schedule">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">이벤트 일정</p>
            <h2>확인된 확률업 구간</h2>
          </div>
          <strong>{formatCountLabel(eventRateUps.events?.length ?? 0, "items")}</strong>
        </div>
        <div className="event-schedule-list">
          {(eventRateUps.events ?? [])
            .slice()
            .sort((a, b) => a.startsAt - b.startsAt || a.id - b.id)
            .map((event) => {
              const status = getEventStatus(event);
              return (
                <article className={`event-schedule-row is-${status}`} key={event.id}>
                  <span className="event-status">{getEventStatusLabel(status)}</span>
                  <b>{event.dragonNames.join(", ")}</b>
                  <span>
                    {formatDateTime(event.startsAt, i18n.locale.code)} - {formatDateTime(event.endsAt, i18n.locale.code)}
                  </span>
                  <em>{status === "active" ? formatEventDday(event) : getEventStatusLabel(status)}</em>
                </article>
              );
            })}
        </div>
      </section>

      <AdSlot placement="bottom" />
      <SiteFooter />
    </main>
  );
}

function SiteFooter() {
  return (
    <footer className="dv-footer">
      <span>DV3 공략은 비공식 팬 공략 사이트입니다.</span>
      <nav aria-label="사이트 정보">
        <a href="/">계산기</a>
        <a href="/events">이벤트</a>
        <a href="/dragons">도감</a>
        <a href="/about">소개</a>
        <a href="/faq">FAQ</a>
        <a href="/privacy">개인정보처리방침</a>
        <a href="/contact">문의</a>
      </nav>
    </footer>
  );
}

function IssueReportLink({
  context,
  dragon,
  compact = false
}: {
  context: string;
  dragon?: Dragon;
  compact?: boolean;
}) {
  return (
    <a
      className={`icon-button issue-report-link ${compact ? "is-compact" : ""}`}
      href={buildIssueReportHref(context, dragon)}
      onClick={() =>
        trackSiteEvent("report_issue", {
          context,
          dragon: dragon?.name,
          code: dragon?.code
        })
      }
    >
      <Flag size={15} />
      오류 제보
    </a>
  );
}

function buildIssueReportHref(context: string, dragon?: Dragon) {
  const pageUrl = window.location.href;
  const subject = `[DV3 공략 오류 제보] ${dragon ? `${dragon.name} ` : ""}${context}`;
  const body = [
    "아래 내용을 채워 보내주시면 확인하겠습니다.",
    "",
    `문제가 있는 화면: ${pageUrl}`,
    dragon ? `드래곤: ${dragon.name} (${dragon.code})` : "",
    `항목: ${context}`,
    "",
    "틀린 내용:",
    "",
    "맞는 내용 또는 참고 자료:"
  ]
    .filter(Boolean)
    .join("\n");
  return `mailto:contact@dv3guide.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function LanguageSelect({ i18n }: { i18n: I18nState }) {
  return (
    <label className="dv-language-select" title="Language">
      <span>언어</span>
      <select value={i18n.locale.code} onChange={(event) => i18n.setLocaleCode(event.target.value)} disabled={i18n.loading}>
        {supportedLocales.map((locale) => (
          <option key={locale.code} value={locale.code}>
            {locale.nativeName} · {locale.code}
          </option>
        ))}
      </select>
    </label>
  );
}

function DragonDirectoryPage({ i18n }: { i18n: I18nState }) {
  const initialFilters = getDirectoryFiltersFromUrl();
  const [query, setQuery] = useState(initialFilters.query);
  const [dropFilter, setDropFilter] = useState(initialFilters.drop);
  const [elementFilter, setElementFilter] = useState(initialFilters.element);
  const [sort, setSort] = useState<DirectorySort>("no");
  const i18nVersion = `${i18n.locale.code}:${Object.keys(i18n.terms).length}`;

  const filteredDragons = useMemo(
    () => filterDirectoryDragons(dragons, query, dropFilter, elementFilter, sort),
    [query, dropFilter, elementFilter, sort, i18nVersion]
  );

  useEffect(() => {
    const normalizedQuery = query.trim();
    if (!normalizedQuery) return;
    const timeoutId = window.setTimeout(() => {
      trackSiteEvent("dragon_search", {
        query: normalizedQuery.slice(0, 60),
        resultCount: filteredDragons.length,
        dropFilter,
        elementFilter,
        sort
      });
    }, 700);
    return () => window.clearTimeout(timeoutId);
  }, [query, filteredDragons.length, dropFilter, elementFilter, sort]);

  usePageMeta({
    title: "드래곤빌리지3 도감 - DV3 공략",
    description: "드래곤빌리지3 드래곤 도감에서 이름, 속성, 획득처, 부화 시간, 희귀도별 정보를 검색하고 교배 계산기로 바로 연결하세요.",
    canonicalPath: "/dragons",
    imagePath: "/assets/dv3-tool-preview.png",
    keywords: ["드래곤빌리지3 도감", "DV3 도감", "드래곤빌리지3 드래곤", "드래곤 속성", "드래곤 획득처"],
    jsonLd: buildDirectoryJsonLd()
  });

  return (
    <main className="dv-app dv-directory-app" key={i18n.locale.code}>
      <header className="dv-head">
        <a className="dv-logo dv-logo-link" href="/">
          <span>DV3 공략</span>
          <strong>드래곤빌리지3 도감</strong>
        </a>
        <div className="dv-nav-pills" aria-label="주요 기능">
          <a href="/">교배 계산기</a>
          <a href="/events">이벤트</a>
          <span>도감</span>
        </div>
        <LanguageSelect i18n={i18n} />
      </header>

      <section className="dv-directory-hero">
        <div>
          <p className="eyebrow">드래곤 도감</p>
          <h1>드래곤을 찾고 바로 목표로 설정</h1>
          <p>이름, 코드, 속성, 획득처로 찾은 뒤 계산기에서 역교배 조합을 바로 볼 수 있습니다.</p>
        </div>
        <strong>
          {filteredDragons.length}/{dragons.length}
        </strong>
      </section>

      <section className="dv-directory-tools" aria-label="도감 필터">
        <label className="dv-field dv-search-field">
          <span>검색</span>
          <div className="dv-input-with-icon">
            <Search size={15} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="이름, 코드, 속성" />
          </div>
        </label>
        <label className="dv-field">
          <span>속성</span>
          <select value={elementFilter} onChange={(event) => setElementFilter(event.target.value)}>
            <option value="all">전체</option>
            {elementFilterOptions.map((element) => (
              <option key={element.code} value={element.code}>
                {element.name}
              </option>
            ))}
          </select>
        </label>
        <label className="dv-field">
          <span>획득처</span>
          <select value={dropFilter} onChange={(event) => setDropFilter(event.target.value)}>
            {dropFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="dv-field">
          <span>정렬</span>
          <select value={sort} onChange={(event) => setSort(event.target.value as DirectorySort)}>
            <option value="no">도감순</option>
            <option value="name">이름순</option>
            <option value="rarity">희귀도순</option>
          </select>
        </label>
      </section>

      <AdSlot placement="top" />

      <section className="dv-directory-grid" aria-label="드래곤 목록">
        {filteredDragons.length > 0 ? (
          filteredDragons.map((dragon) => <DragonDirectoryCard dragon={dragon} key={dragon.id} />)
        ) : (
          <p className="empty">조건에 맞는 드래곤이 없습니다.</p>
        )}
      </section>

      <AdSlot placement="bottom" />
      <SiteFooter />
    </main>
  );
}

function DragonDirectoryCard({ dragon }: { dragon: Dragon }) {
  const hasRecipe = hasBreedingRecipe(dragon);

  return (
    <article className={`dragon-card dragon-card-${getPrimaryElementCode(dragon)}`}>
      <a
        className="dragon-card-link"
        href={getDragonDetailPath(dragon)}
        aria-label={`${dragon.name} 상세 보기`}
        onClick={() => rememberDragonInterest(dragon)}
      />
      <div className="dragon-card-art">
        <DragonEgg dragon={dragon} size="md" />
        <DragonArt dragon={dragon} size="md" />
      </div>
      <div className="dragon-card-body">
        <div className="dragon-card-title">
          <h2>{dragon.name}</h2>
        </div>
        <ElementBadgeList dragon={dragon} />
        <dl className="dragon-card-meta">
          <div>
            <dt>획득처</dt>
            <dd>{getDropLabel(dragon.drop)}</dd>
          </div>
          <div>
            <dt>부화</dt>
            <dd>{formatDuration(dragon.hatchingSeconds)}</dd>
          </div>
          <div>
            <dt>희귀도</dt>
            <dd>{dragon.rarity}</dd>
          </div>
        </dl>
      </div>
      <a className="icon-button dragon-card-action" href={getCalculatorTargetPath(dragon)} onClick={() => rememberDragonInterest(dragon)}>
        {hasRecipe ? <Target size={15} /> : <ShoppingBag size={15} />}
        {hasRecipe ? "목표로 설정" : "획득처 확인"}
      </a>
    </article>
  );
}

function DragonDetailPage({ dragon, i18n }: { dragon: Dragon; i18n: I18nState }) {
  const hasRecipe = hasBreedingRecipe(dragon);
  const options: BreedingOptions = useMemo(
    () => ({ userLevel: 99, allowSameSpecies: false, activeRateUpDids: new Set<number>() }),
    []
  );
  const targetCombos = useMemo(
    () => findParentCombos(dragons, dragon.id, data.tierFixedRateMax, options),
    [dragon.id, options]
  );
  const parentOutcomes = useMemo(() => getParentOutcomePreview(dragon, options), [dragon, options]);
  const obtainText = getObtainText(dragon) ?? getDropLabel(dragon.drop);
  const requirementText = getParentRequirementText(dragon);
  const bestCombo = targetCombos[0];
  const isRateUp = activeEventRateUpIds.has(dragon.id);
  const dragonFaqItems = useMemo(
    () => getDragonFaqItems(dragon, targetCombos, parentOutcomes, obtainText),
    [dragon, targetCombos, parentOutcomes, obtainText]
  );
  const detailDescription =
    targetCombos.length > 0
      ? `${dragon.name} 얻는법, 추천 교배식, 성공 확률, 부화 시간, 속성, 능력치와 성장 단계 이미지를 DV3 공략 도감에서 확인하세요.`
      : `${dragon.name} 얻는법, 획득처, 부화 시간, 속성, 능력치와 성장 단계 이미지를 DV3 공략 도감에서 확인하세요.`;

  usePageMeta({
    title: `${dragon.name} 얻는법 · 교배식 · 도감 - DV3 공략`,
    description: detailDescription,
    canonicalPath: getDragonDetailPath(dragon),
    imagePath: getPortraitPath(dragon),
    keywords: [
      "드래곤빌리지3",
      "DV3 공략",
      `${dragon.name} 얻는법`,
      `${dragon.name} 교배식`,
      `${dragon.name} 교배`,
      `${dragon.name} 도감`,
      `${dragon.name} 획득처`,
      `${dragon.name} 부화 시간`,
      ...dragon.elementNames.map((elementName) => `${elementName} 드래곤`)
    ],
    jsonLd: buildDragonDetailJsonLd(dragon, dragonFaqItems)
  });

  useEffect(() => {
    rememberDragonInterest(dragon);
  }, [dragon.id]);

  return (
    <main className="dv-app dv-dragon-detail-app" key={i18n.locale.code}>
      <header className="dv-head">
        <a className="dv-logo dv-logo-link" href="/dragons">
          <span>DV3 공략</span>
          <strong>드래곤빌리지3 도감</strong>
        </a>
        <div className="dv-nav-pills" aria-label="주요 기능">
          <a href="/">교배 계산기</a>
          <a href="/events">이벤트</a>
          <a href="/dragons">도감</a>
        </div>
        <LanguageSelect i18n={i18n} />
      </header>

      <section className={`dragon-detail-hero dragon-card-${getPrimaryElementCode(dragon)}`}>
        <div className="dragon-detail-art">
          <DragonEgg dragon={dragon} size="lg" />
          <DragonArt dragon={dragon} size="lg" />
        </div>
        <div className="dragon-detail-title">
          <a href="/dragons">도감으로 돌아가기</a>
          <h1>{dragon.name}</h1>
          <ElementBadgeList dragon={dragon} />
          <p>{formatDragonText(dragon.ecology.obtain)}</p>
        </div>
        <div className="dragon-detail-actions">
          <a
            className="icon-button dragon-detail-target"
            href={getCalculatorTargetPath(dragon)}
            onClick={() => {
              rememberDragonInterest(dragon);
              trackSiteEvent("select_target", { dragon: dragon.name, code: dragon.code, source: "dragon_detail" });
            }}
          >
            {hasRecipe ? <Target size={16} /> : <ShoppingBag size={16} />}
            {hasRecipe ? "목표로 설정" : "획득처 확인"}
          </a>
          <IssueReportLink context="도감 상세" dragon={dragon} compact />
        </div>
      </section>

      <DragonDetailSummary
        dragon={dragon}
        bestCombo={bestCombo}
        parentOutcomeCount={parentOutcomes.length}
        obtainText={obtainText}
        isRateUp={isRateUp}
      />

      <DragonFormsGallery dragon={dragon} />

      <AdSlot placement="top" />

      <section className="dragon-detail-grid">
        <DragonDetailSection title="기본 정보">
          <div className="dragon-detail-facts">
            <DragonFact label="획득처" value={obtainText} />
            <DragonFact label="희귀도" value={String(dragon.rarity)} />
            <DragonFact label="티어" value={String(dragon.tier)} />
            <DragonFact label="필요 레벨" value={String(Math.max(dragon.requireUserLevel, dragon.requireUserLevelByElement))} />
            <DragonFact label="부화 시간" value={formatDuration(dragon.hatchingSeconds)} />
            <DragonFact label="교배 시간" value={formatDuration(dragon.breedingSeconds)} />
            <DragonFact label="부모 가능" value={dragon.canBreedAsParent ? "가능" : "불가"} />
            {requirementText && <DragonFact label="교배 조건" value={requirementText} />}
          </div>
        </DragonDetailSection>

        <DragonDetailSection title="전투 정보">
          <div className="dragon-detail-facts">
            <DragonFact label="특성" value={dragon.abilityName} />
            <DragonFact label="기본 스킬" value={dragon.baseMoveName} />
            <DragonFact label="궁극기" value={dragon.ultMoveName} />
          </div>
        </DragonDetailSection>

        <DragonDetailSection title="능력치">
          <div className="dragon-stat-list">
            {[
              ["HP", "hp"],
              ["ATK", "atk"],
              ["DEF", "def"],
              ["MAG", "mag"],
              ["MR", "mr"],
              ["SPD", "spd"]
            ].map(([label, key]) => {
              const statKey = key as keyof Dragon["stats"];
              const value = dragon.stats[statKey];
              const max = getStatMax(statKey);
              return (
                <div className="dragon-stat-row" key={key}>
                  <span>{label}</span>
                  <div className="dragon-stat-bar">
                    <i style={{ width: `${Math.max(5, (value / max) * 100)}%` }} />
                  </div>
                  <strong>{value.toLocaleString()}</strong>
                </div>
              );
            })}
            <div className="dragon-stat-total">
              <span>총합</span>
              <strong>{dragon.stats.sum.toLocaleString()}</strong>
            </div>
          </div>
        </DragonDetailSection>

        <DragonDetailSection title="생태 정보">
          <div className="dragon-detail-facts">
            <DragonFact label="알 설명" value={dragon.ecology.egg} />
            <DragonFact label="타입" value={dragon.ecology.type} />
            <DragonFact label="먹이" value={dragon.ecology.feed} />
            <DragonFact label="크기" value={dragon.ecology.size} />
            <DragonFact label="무게" value={dragon.ecology.weight} />
          </div>
        </DragonDetailSection>
      </section>

      <section className="dragon-detail-breeding">
        <DragonDetailSection title="이 드래곤을 얻는 조합">
          {targetCombos.length > 0 ? (
            <div className="detail-combo-list">
              {targetCombos.slice(0, 8).map((combo) => (
                <article className="detail-combo-row" key={`${combo.parentA.id}-${combo.parentB.id}`}>
                  <DragonMini dragon={combo.parentA} />
                  <Plus size={15} />
                  <DragonMini dragon={combo.parentB} />
                  <ArrowRight size={15} />
                  <ChanceValue result={combo.target} />
                  <span>{formatDuration(combo.expectedSeconds)}</span>
                  <span>기대 {formatAttempts(combo.target.chance)}</span>
                </article>
              ))}
            </div>
          ) : (
            <p className="empty">{obtainText || "현재 데이터 기준으로 교배 조합이 없습니다."}</p>
          )}
        </DragonDetailSection>

        <DragonDetailSection title="부모로 썼을 때">
          {parentOutcomes.length > 0 ? (
            <div className="detail-combo-list">
              {parentOutcomes.map(({ partner, result }) => (
                <article className="detail-combo-row" key={`${partner.id}-${result.dragon.id}`}>
                  <DragonMini dragon={dragon} />
                  <Plus size={15} />
                  <DragonMini dragon={partner} />
                  <ArrowRight size={15} />
                  <DragonMini dragon={result.dragon} />
                  <ChanceValue result={result} />
                </article>
              ))}
            </div>
          ) : (
            <p className="empty">부모로 사용할 수 없거나, 확인 가능한 결과가 없습니다.</p>
          )}
        </DragonDetailSection>
      </section>

      <DragonDetailSection title="빠른 답변">
        <div className="dragon-quick-answer-list">
          {dragonFaqItems.map((item) => (
            <article className="dragon-quick-answer" key={item.question}>
              <h3>{item.question}</h3>
              <p>{item.answer}</p>
            </article>
          ))}
        </div>
      </DragonDetailSection>

      <DragonDetailSection title="관련 공략 링크">
        <div className="detail-combo-list">
          <a className="icon-button" href={getCalculatorTargetPath(dragon)}>
            <Target size={15} />
            {dragon.name} 목표 계산기 열기
          </a>
          <a className="icon-button" href={`/dragons?element=${encodeURIComponent(getPrimaryElementCode(dragon))}`}>
            <Search size={15} />
            같은 속성 드래곤 보기
          </a>
          {getRelatedDragons(dragon).map((related) => (
            <a className="icon-button" href={getDragonDetailPath(related)} key={related.id}>
              {related.name} 도감
            </a>
          ))}
        </div>
      </DragonDetailSection>

      <AdSlot placement="bottom" />
      <SiteFooter />
    </main>
  );
}

function DragonDetailSummary({
  dragon,
  bestCombo,
  parentOutcomeCount,
  obtainText,
  isRateUp
}: {
  dragon: Dragon;
  bestCombo?: ParentCombo;
  parentOutcomeCount: number;
  obtainText: string;
  isRateUp: boolean;
}) {
  const rateUpText = isRateUp
    ? `현재 이벤트 확률업 대상입니다${formatEventRateUpPeriod(activeEventRateUpEvents, "ko") ? ` · ${formatEventRateUpPeriod(activeEventRateUpEvents, "ko")}까지` : ""}.`
    : "현재 이벤트 확률업 대상은 아닙니다.";

  return (
    <section className="dragon-seo-summary" aria-label={`${dragon.name} 요약`}>
      <article>
        <span>얻는법</span>
        <strong>{bestCombo ? "교배 가능" : obtainText}</strong>
        <p>{bestCombo ? `${bestCombo.parentA.name} + ${bestCombo.parentB.name} 조합부터 확인하세요.` : `${dragon.name}은 ${obtainText} 방식으로 확인됩니다.`}</p>
      </article>
      <article>
        <span>추천 조합</span>
        <strong>{bestCombo ? `${bestCombo.target.chanceText} · ${formatDuration(bestCombo.expectedSeconds)}` : "교배식 없음"}</strong>
        <p>{bestCombo ? `${bestCombo.parentA.name} + ${bestCombo.parentB.name} → ${dragon.name}` : "계산기에서는 획득처 안내로 표시합니다."}</p>
      </article>
      <article>
        <span>부모 활용</span>
        <strong>{dragon.canBreedAsParent ? `${parentOutcomeCount}개 결과` : "부모 불가"}</strong>
        <p>{dragon.canBreedAsParent ? `${dragon.name}을 부모로 쓰는 대표 결과를 아래에서 확인할 수 있습니다.` : "현재 데이터 기준으로 부모 교배에 사용할 수 없습니다."}</p>
      </article>
      <article>
        <span>이벤트</span>
        <strong>{isRateUp ? "확률업 적용" : "기본 확률"}</strong>
        <p>{rateUpText}</p>
      </article>
    </section>
  );
}

function DragonNotFoundPage({ i18n }: { i18n: I18nState }) {
  usePageMeta({
    title: "드래곤을 찾을 수 없습니다 - DV3 공략",
    description: "DV3 공략 도감에서 확인할 수 없는 드래곤 주소입니다. 전체 도감에서 드래곤 이름과 속성으로 다시 검색하세요.",
    canonicalPath: "/dragons",
    imagePath: "/assets/dv3-tool-preview.png",
    keywords: ["드래곤빌리지3 도감", "DV3 공략"]
  });

  return (
    <main className="dv-app dv-static-app" key={i18n.locale.code}>
      <header className="dv-head">
        <a className="dv-logo dv-logo-link" href="/dragons">
          <span>DV3 공략</span>
          <strong>드래곤빌리지3 도감</strong>
        </a>
        <div className="dv-nav-pills" aria-label="주요 기능">
          <a href="/">교배 계산기</a>
          <a href="/events">이벤트</a>
          <a href="/dragons">도감</a>
        </div>
        <LanguageSelect i18n={i18n} />
      </header>
      <section className="dv-static-page">
        <p className="dv-static-kicker">dv3guide.com</p>
        <h1>드래곤을 찾을 수 없습니다</h1>
        <p>주소가 바뀌었거나 아직 도감 데이터에 없는 드래곤입니다.</p>
        <p>
          <a className="icon-button" href="/dragons">도감으로 돌아가기</a>
        </p>
      </section>
      <SiteFooter />
    </main>
  );
}

function DragonDetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="dragon-detail-section">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function DragonFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="dragon-fact">
      <span>{label}</span>
      <strong>{formatDragonText(value)}</strong>
    </div>
  );
}

function DragonFormsGallery({ dragon }: { dragon: Dragon }) {
  const code = dragon.code.toLowerCase();
  const normalStages = getOrderedFormStages(formData.normal[code]);
  const chromaStages = getOrderedFormStages(formData.chroma[code]);
  const chromaIconStages = getOrderedFormStages(formData.chromaIcons[code]);
  const hasChroma = chromaStages.length > 0 || chromaIconStages.length > 0;
  const groups: Array<{ variant: DragonFormVariant; label: string; stages: DragonFormStage[] }> = [
    { variant: "normal", label: "일반", stages: normalStages }
  ];

  if (chromaStages.length > 0) groups.push({ variant: "chroma", label: "크로마", stages: chromaStages });
  if (normalStages.length === 0) return null;

  return (
    <section className="dragon-forms-section">
      <div className="panel-heading">
        <div>
          <p className="eyebrow">성장 단계</p>
          <h2>모습</h2>
        </div>
        {hasChroma && <strong>크로마</strong>}
      </div>
      <div className="dragon-form-track">
        {groups.map((group) => (
          <article className="dragon-form-slide" key={group.variant}>
            <div className="dragon-form-slide-head">
              <strong>{group.label}</strong>
              <span>{group.stages.length}/3</span>
            </div>
            <div className="dragon-stage-list">
              {group.stages.map((stage) => (
                <figure className="dragon-stage-card" key={`${group.variant}-${stage}`}>
                  <img
                    src={getDragonFormPath(dragon, group.variant, stage)}
                    alt={`${dragon.name} ${group.label} ${formStageLabels[stage]}`}
                    loading="eager"
                    decoding="async"
                  />
                  <figcaption>{formStageLabels[stage]}</figcaption>
                </figure>
              ))}
            </div>
          </article>
        ))}
      </div>
      {chromaIconStages.length > 0 && (
        <div className="dragon-chroma-preview">
          <div className="dragon-chroma-preview-head">
            <strong>크로마 미리보기</strong>
            <span>아이콘 기준</span>
          </div>
          <div className="dragon-chroma-icon-list">
            {chromaIconStages.map((stage) => (
              <figure className="dragon-chroma-icon-card" key={`chroma-icon-${stage}`}>
                <img
                  src={getDragonChromaIconPath(dragon, stage)}
                  alt={`${dragon.name} 크로마 ${formStageLabels[stage]}`}
                  loading="eager"
                  decoding="async"
                />
                <figcaption>{formStageLabels[stage]}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function ElementBadgeList({ dragon }: { dragon: Dragon }) {
  return (
    <div className="dragon-card-tags" aria-label="속성">
      {dragon.elementCodes.map((code, index) => (
        <ElementBadge code={code} name={dragon.elementNames[index] ?? code} key={`${dragon.id}-${code}`} />
      ))}
    </div>
  );
}

function ElementBadge({ code, name }: { code: string; name: string }) {
  return (
    <span className="element-badge" title={name}>
      <img src={`/assets/element_icons/${code}.webp`} alt="" loading="lazy" decoding="async" />
      <b>{name}</b>
    </span>
  );
}

function StaticPage({ page, i18n }: { page: StaticPageId; i18n: I18nState }) {
  useAdSenseScript();
  const content = staticPageContent[page];
  usePageMeta({
    title: `${content.title} - DV3 공략`,
    description: content.sections[0]?.body[0] ?? "DV3 공략 사이트 정보 페이지입니다.",
    canonicalPath: `/${page}`,
    imagePath: "/assets/dv3-tool-preview.png",
    keywords: ["DV3 공략", "드래곤빌리지3 공략", content.title],
    jsonLd: page === "faq" ? [...buildStaticPageJsonLd(page, content), buildFaqJsonLd(siteFaqItems)] : buildStaticPageJsonLd(page, content)
  });

  return (
    <main className="dv-app dv-static-app" key={i18n.locale.code}>
      <header className="dv-head">
        <a className="dv-logo dv-logo-link" href="/">
          <span>드래곤빌리지3</span>
          <strong>DV3 공략</strong>
        </a>
        <div className="dv-nav-pills" aria-label="주요 기능">
          <a href="/">교배 계산기</a>
          <a href="/events">이벤트</a>
          <a href="/dragons">도감</a>
        </div>
        <LanguageSelect i18n={i18n} />
      </header>
      <article className="dv-static-page">
        <p className="dv-static-kicker">dv3guide.com</p>
        <h1>{content.title}</h1>
        {content.sections.map((section) => (
          <section key={section.heading}>
            <h2>{section.heading}</h2>
            {section.body.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </section>
        ))}
      </article>
      <SiteFooter />
    </main>
  );
}

const staticPageContent: Record<
  StaticPageId,
  { title: string; sections: Array<{ heading: string; body: string[] }> }
> = {
  about: {
    title: "사이트 소개",
    sections: [
      {
        heading: "비공식 팬 공략 사이트",
        body: [
          "DV3 공략은 드래곤빌리지3 이용자가 교배 조합, 역교배, 목표 드래곤까지의 경로를 빠르게 확인할 수 있도록 만든 비공식 팬 공략 사이트입니다.",
          "이 사이트는 게임의 공식 서비스, 운영사, 퍼블리셔와 직접적인 관계가 없습니다."
        ]
      },
      {
        heading: "제공 기능",
        body: [
          "부모 드래곤 두 마리로 나올 수 있는 결과를 계산하고, 원하는 드래곤을 얻기 위한 부모 조합을 확인할 수 있습니다.",
          "보유 드래곤을 기준으로 목표 드래곤까지 이어지는 경로 탐색 기능도 함께 제공합니다."
        ]
      }
    ]
  },
  privacy: {
    title: "개인정보처리방침",
    sections: [
      {
        heading: "수집하는 정보",
        body: [
          "DV3 공략은 회원가입을 제공하지 않으며, 이름, 전화번호, 주소 같은 직접 식별 정보를 자체적으로 수집하지 않습니다.",
          "다만 안정적인 서비스 운영, 통계 확인, 광고 제공을 위해 접속 로그, 브라우저 정보, 대략적인 지역 정보, 쿠키 또는 유사 기술이 사용될 수 있습니다.",
          "사이트 개선을 위해 페이지 조회, 목표 드래곤 선택, 도감 검색어, 오류 제보 클릭 같은 이용 이벤트가 분석 도구로 전달될 수 있습니다."
        ]
      },
      {
        heading: "광고와 쿠키",
        body: [
          "사이트 운영비를 마련하기 위해 Google AdSense 또는 국내 광고 플랫폼을 사용할 수 있습니다.",
          "광고 제공자는 맞춤 광고, 광고 성과 측정, 부정 사용 방지를 위해 쿠키를 사용할 수 있으며, 사용자는 브라우저 설정이나 광고 제공자의 설정 페이지에서 맞춤 광고를 제한할 수 있습니다."
        ]
      },
      {
        heading: "문의",
        body: [
          "개인정보 또는 사이트 운영 관련 문의는 contact@dv3guide.com 으로 연락해 주세요.",
          "문의 메일을 보내는 경우 답변을 위해 이메일 주소와 문의 내용이 처리될 수 있습니다."
        ]
      }
    ]
  },
  contact: {
    title: "문의",
    sections: [
      {
        heading: "오류 제보",
        body: [
          "교배 확률, 드래곤 이름, 이미지, 획득처 정보가 잘못된 경우 contact@dv3guide.com 으로 알려주세요.",
          "제보할 때 드래곤 이름과 확인한 화면을 함께 적어주면 더 빠르게 확인할 수 있습니다.",
          "계산기와 도감 상세 페이지의 오류 제보 버튼을 누르면 화면 주소와 항목이 메일 본문에 자동으로 들어갑니다."
        ]
      },
      {
        heading: "광고 및 운영 문의",
        body: [
          "광고, 제휴, 사이트 운영 관련 문의도 같은 주소로 받을 예정입니다.",
          "DV3 공략은 무료 운영을 목표로 하며, 광고는 계산기 사용을 방해하지 않는 위치에 제한적으로 배치합니다."
        ]
      }
    ]
  },
  faq: {
    title: "자주 묻는 질문",
    sections: siteFaqItems.map((item) => ({
      heading: item.question,
      body: [item.answer]
    }))
  }
};

// 교배로 안 나오는(또는 상점/가챠/레이드로 얻는) 드래곤의 획득처 안내 문구
function getObtainText(dragon: Dragon): string | null {
  if (dragon.buyable && dragon.buyItems) {
    const [itemId, qty] = dragon.buyItems.split(";");
    const amount = Number(qty).toLocaleString();
    if (itemId === "6002010001") return `상점에서 구매 · 골드 ${amount}`;
    if (itemId === "6001000001" || itemId === "6001020001") return `상점에서 구매 · 다이아 ${amount}`;
    return "상점에서 구매 가능";
  }
  switch (dragon.drop) {
    case "gacha_normal":
      return "일반 가챠로 획득";
    case "gacha_pickup":
      return "픽업 가챠로 획득";
    case "raid":
      return "레이드 보상으로 획득";
    case "sell":
      return "상점에서 판매";
    default:
      return null;
  }
}

function HeroAnswer({
  mode,
  target,
  bestCombo,
  parentA,
  parentB,
  bestResults,
  fastestResult,
  comboCount,
  resultCount,
  warnings,
  onRecipeTargetSelect
}: {
  mode: MainMode;
  target: Dragon;
  bestCombo?: ParentCombo;
  parentA: Dragon;
  parentB: Dragon;
  bestResults: BreedingResult[];
  fastestResult?: BreedingResult;
  comboCount: number;
  resultCount: number;
  warnings: string[];
  onRecipeTargetSelect: (dragon: Dragon) => void;
}) {
  if (mode === "target") {
    if (!bestCombo) {
      const obtain = getObtainText(target);
      if (obtain) {
        return (
          <div className="dv-answer dv-answer-obtain">
            <span className="dv-answer-tag">교배로는 나오지 않아요</span>
            <div className="dv-obtain">
              <ShoppingBag size={17} />
              {obtain}
            </div>
          </div>
        );
      }
      return <div className="dv-answer dv-answer-empty">조건에 맞는 부모 조합이 없어요.</div>;
    }
    return (
      <div className="dv-answer">
        <span className="dv-answer-tag">최적 조합 · {formatOutOfCount(comboCount)}</span>
        <div className="dv-answer-route">
          <DragonTokenButton dragon={bestCombo.parentA} onClick={() => onRecipeTargetSelect(bestCombo.parentA)} />
          <Plus size={15} />
          <DragonTokenButton dragon={bestCombo.parentB} onClick={() => onRecipeTargetSelect(bestCombo.parentB)} />
          <ArrowRight size={18} />
          <DragonToken dragon={target} useEgg />
        </div>
        <div className="dv-answer-stats">
          <div className="dv-stat">
            <ChanceValue result={bestCombo.target} />
            <span>성공 확률</span>
          </div>
          <div className="dv-stat">
            <strong>{formatAttempts(bestCombo.target.chance)}</strong>
            <span>기대 횟수</span>
          </div>
          <div className="dv-stat">
            <strong><Clock3 size={13} />{formatDuration(bestCombo.expectedSeconds)}</strong>
            <span>부화 시간</span>
          </div>
        </div>
      </div>
    );
  }

  const top = bestResults[0];
  return (
    <div className="dv-answer">
      <span className="dv-answer-tag">부화 결과 · {formatCountLabel(resultCount, "species")}</span>
      {warnings.length > 0 && <span className="dv-answer-warn">{warnings[0]}</span>}
      <div className="dv-answer-route">
        <DragonTokenButton dragon={parentA} onClick={() => onRecipeTargetSelect(parentA)} />
        <Plus size={15} />
        <DragonTokenButton dragon={parentB} onClick={() => onRecipeTargetSelect(parentB)} />
        <ArrowRight size={18} />
        <div className="dv-answer-results">
          {bestResults.length > 0 ? (
            bestResults.map((result) => (
              <DragonTokenButton key={result.dragon.id} dragon={result.dragon} onClick={() => onRecipeTargetSelect(result.dragon)} useEgg />
            ))
          ) : (
            <span className="dv-answer-empty">결과 없음</span>
          )}
        </div>
      </div>
      {top && (
        <div className="dv-answer-stats">
          <div className="dv-stat">
            <ChanceValue result={top} />
            <span>가장 높은 성공 확률</span>
          </div>
          {fastestResult && (
            <div className="dv-stat">
              <strong><Clock3 size={13} />{formatDuration(fastestResult.hatchingSeconds)}</strong>
              <span>최단 시간</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SelectedDragonControl({
  eyebrow,
  title,
  dragon,
  value,
  dragons: selectDragons,
  onChange
}: {
  eyebrow: string;
  title: string;
  dragon: Dragon;
  value: number;
  dragons: Dragon[];
  onChange: (value: number) => void;
}) {
  return (
    <section className="dv-selected-dragon">
      <div className="dv-selected-heading">
        <span>{eyebrow}</span>
        <strong>{title}</strong>
      </div>
      <div className="dv-selected-body">
        <div className="dv-selected-art">
          <DragonEgg dragon={dragon} size="md" />
          <DragonArt dragon={dragon} size="md" />
        </div>
        <b>{dragon.name}</b>
      </div>
      <select value={value} onChange={(event) => onChange(Number(event.target.value))}>
        {[...selectDragons]
          .sort((a, b) => compareLocalized(a.name, b.name))
          .map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
      </select>
    </section>
  );
}

function TargetRecipeBoard({
  target,
  combos,
  allComboCount,
  onParentRecipeSelect
}: {
  target: Dragon;
  combos: ParentCombo[];
  allComboCount: number;
  onParentRecipeSelect: (dragon: Dragon) => void;
}) {
  const best = combos[0];
  const obtain = getObtainText(target);
  const [visibleComboCount, setVisibleComboCount] = useState(comboPageSize);
  const visibleCombos = combos.slice(0, visibleComboCount);
  const hiddenComboCount = Math.max(0, combos.length - visibleCombos.length);

  useEffect(() => {
    setVisibleComboCount(comboPageSize);
  }, [target.id, combos]);

  return (
    <section className="dv-board">
      <div className="dv-board-header">
        <div>
          <span>목표 기준</span>
          <h2>{target.name} 역교배</h2>
        </div>
        <strong>{formatCountLabel(allComboCount, "combos")}</strong>
      </div>

      {best ? (
        <div className="dv-feature-route">
          <div className="dv-feature-target">
            <DragonEgg dragon={target} size="lg" />
            <div>
              <span>추천 루트</span>
              <strong>{target.name}</strong>
              <ChanceBadge result={best.target} />
            </div>
          </div>
          <div className="dv-route-line">
            <DragonTokenButton dragon={best.parentA} onClick={() => onParentRecipeSelect(best.parentA)} />
            <Plus size={17} />
            <DragonTokenButton dragon={best.parentB} onClick={() => onParentRecipeSelect(best.parentB)} />
            <ArrowRight size={19} />
            <DragonToken dragon={target} useEgg />
          </div>
        </div>
      ) : obtain ? (
        <p className="dv-empty">
          <ShoppingBag size={15} />
          교배로는 나오지 않아요 · {obtain}
        </p>
      ) : (
        <p className="dv-empty">조건에 맞는 부모 조합이 없습니다.</p>
      )}

      <div className="dv-recipe-table">
        <div className="dv-recipe-head">
          <span>#</span>
          <span>부모 1</span>
          <span>부모 2</span>
          <span>결과</span>
          <span>확률</span>
          <span>부화</span>
          <span>기대</span>
        </div>
        {visibleCombos.map((combo, index) => (
          <article className="dv-recipe-row" key={`${combo.parentA.id}-${combo.parentB.id}-${combo.target.dragon.id}`}>
            <span className="dv-row-rank">{index + 1}</span>
            <DragonTokenButton dragon={combo.parentA} onClick={() => onParentRecipeSelect(combo.parentA)} />
            <DragonTokenButton dragon={combo.parentB} onClick={() => onParentRecipeSelect(combo.parentB)} />
            <DragonToken dragon={combo.target.dragon} useEgg />
            <ChanceValue result={combo.target} />
            <span>{formatDuration(combo.expectedSeconds)}</span>
            <span>{formatAttempts(combo.target.chance)}</span>
          </article>
        ))}
      </div>
      {combos.length > comboPageSize && (
        <div className="dv-list-more">
          <span>
            {formatShownCount(visibleCombos.length, combos.length)}
          </span>
          {hiddenComboCount > 0 ? (
            <button
              type="button"
              onClick={() => setVisibleComboCount((count) => Math.min(count + comboPageSize, combos.length))}
            >
              {formatMoreCount(Math.min(comboPageSize, hiddenComboCount))}
            </button>
          ) : (
            <button type="button" onClick={() => setVisibleComboCount(comboPageSize)}>
              접기
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function ParentResultBoard({
  parentA,
  parentB,
  results,
  allResultCount,
  warnings,
  onRecipeTargetSelect
}: {
  parentA: Dragon;
  parentB: Dragon;
  results: BreedingResult[];
  allResultCount: number;
  warnings: string[];
  onRecipeTargetSelect: (dragon: Dragon) => void;
}) {
  return (
    <section className="dv-board">
      <div className="dv-board-header">
        <div>
          <span>부모 기준</span>
          <h2>
            {parentA.name} <Plus size={17} /> {parentB.name}
          </h2>
        </div>
        <strong>{formatCountLabel(allResultCount, "candidates")}</strong>
      </div>

      {warnings.length > 0 && (
        <div className="dv-warning-row">
          {warnings.map((warning) => (
            <span key={warning}>{warning}</span>
          ))}
        </div>
      )}

      <div className="dv-feature-route dv-parent-route">
        <div className="dv-route-line">
          <DragonTokenButton dragon={parentA} onClick={() => onRecipeTargetSelect(parentA)} />
          <Plus size={17} />
          <DragonTokenButton dragon={parentB} onClick={() => onRecipeTargetSelect(parentB)} />
          <ArrowRight size={19} />
          <div className="dv-top-results">
            {results.slice(0, 3).map((result) => (
              <DragonTokenButton key={result.dragon.id} dragon={result.dragon} onClick={() => onRecipeTargetSelect(result.dragon)} useEgg />
            ))}
          </div>
        </div>
      </div>

      <div className="dv-recipe-table dv-result-table">
        <div className="dv-recipe-head">
          <span>#</span>
          <span>드래곤</span>
          <span>알</span>
          <span>확률</span>
          <span>시간</span>
          <span>기대</span>
        </div>
        {results.slice(0, 40).map((result, index) => (
          <article className="dv-recipe-row" key={result.dragon.id}>
            <span className="dv-row-rank">{index + 1}</span>
            <DragonTokenButton dragon={result.dragon} onClick={() => onRecipeTargetSelect(result.dragon)} />
            <DragonEgg dragon={result.dragon} size="xs" />
            <ChanceValue result={result} />
            <span>{formatDuration(result.hatchingSeconds)}</span>
            <span>{formatAttempts(result.chance)}</span>
          </article>
        ))}
      </div>
    </section>
  );
}

function MultiTargetBoard({
  selectedTargets,
  addTargetId,
  addTargetOptions,
  combos,
  allComboCount,
  onAddTargetIdChange,
  onAddTarget,
  onRemoveTarget,
  onRecipeTargetSelect
}: {
  selectedTargets: Dragon[];
  addTargetId: number;
  addTargetOptions: Dragon[];
  combos: MultiTargetCombo[];
  allComboCount: number;
  onAddTargetIdChange: (did: number) => void;
  onAddTarget: () => void;
  onRemoveTarget: (did: number) => void;
  onRecipeTargetSelect: (dragon: Dragon) => void;
}) {
  const selectedIds = new Set(selectedTargets.map((dragon) => dragon.id));
  const best = combos[0];
  const singleTarget = selectedTargets.length === 1 ? selectedTargets[0] : null;
  const singleTargetObtain = singleTarget ? getObtainText(singleTarget) : null;
  const unavailableTargets = selectedTargets.filter((dragon) => getObtainText(dragon));
  const unavailableTargetText = unavailableTargets
    .map((dragon) => `${dragon.name}(${getObtainText(dragon)})`)
    .join(", ");

  return (
    <section className="dv-board dv-multi-board">
      <div className="multi-target-tools">
        <label className="dv-field">
          <span>목표 추가</span>
          <select value={addTargetId} onChange={(event) => onAddTargetIdChange(Number(event.target.value))}>
            {[...addTargetOptions]
              .sort((a, b) => compareLocalized(a.name, b.name))
              .map((dragon) => (
                <option key={dragon.id} value={dragon.id} disabled={selectedIds.has(dragon.id)}>
                  {dragon.name}
                </option>
              ))}
          </select>
        </label>
        <button className="dv-reset-button" onClick={onAddTarget} type="button" disabled={selectedIds.has(addTargetId)}>
          <Plus size={15} />추가
        </button>
      </div>

      <div className="multi-target-chips">
        {selectedTargets.map((dragon) => (
          <button className="multi-target-chip" key={dragon.id} onClick={() => onRemoveTarget(dragon.id)} type="button">
            <DragonEgg dragon={dragon} size="xs" />
            {dragon.name}
            <X size={13} />
          </button>
        ))}
      </div>

      {selectedTargets.length < 2 && (
        singleTargetObtain ? (
          <p className="dv-empty">
            <ShoppingBag size={15} />
            교배로는 나오지 않아요 · {singleTargetObtain}
          </p>
        ) : (
          <p className="dv-empty">목표를 하나 더 추가하면 여러 드래곤을 동시에 노리는 부모 조합을 비교할 수 있습니다.</p>
        )
      )}

      {selectedTargets.length >= 2 && unavailableTargets.length > 0 && (
        <p className="dv-empty">
          <ShoppingBag size={15} />
          교배로는 나오지 않는 목표 포함 · {unavailableTargetText}
        </p>
      )}

      {best ? (
        <div className="multi-feature">
          <div>
            <span>추천 부모</span>
            <strong>
              {best.parentA.name} + {best.parentB.name}
            </strong>
          </div>
          <div className="multi-feature-targets">
            {best.targets.map((target) => (
              <button key={target.dragon.id} type="button" onClick={() => onRecipeTargetSelect(target.dragon)} title={`${target.dragon.name} 교배식 보기`}>
                <DragonEgg dragon={target.dragon} size="xs" />
                {target.dragon.name}
                <ChanceBadge result={target} />
              </button>
            ))}
          </div>
          <div className="multi-feature-score">
            <b>{best.hitCount}/{best.totalTargets}</b>
            <TotalChanceValue combo={best} />
            <span>시간 {formatDuration(best.expectedSeconds)}</span>
            <span>기대 {formatAttempts(best.totalChance)}</span>
          </div>
        </div>
      ) : singleTargetObtain || unavailableTargets.length > 0 ? null : (
        <p className="dv-empty">선택한 목표를 모두 포함하는 부모 조합을 찾지 못했습니다.</p>
      )}

      <div className="dv-recipe-table dv-multi-table">
        <div className="dv-recipe-head">
          <span>#</span>
          <span>부모 1</span>
          <span>부모 2</span>
          <span>목표 포함</span>
          <span>합산 확률</span>
          <span>시간</span>
          <span>기대</span>
        </div>
        {combos.slice(0, 30).map((combo, index) => (
          <article className="dv-recipe-row dv-multi-row" key={`${combo.parentA.id}-${combo.parentB.id}-${combo.targets.map((item) => item.dragon.id).join("-")}`}>
            <span className="dv-row-rank">{index + 1}</span>
            <DragonTokenButton dragon={combo.parentA} onClick={() => onRecipeTargetSelect(combo.parentA)} />
            <DragonTokenButton dragon={combo.parentB} onClick={() => onRecipeTargetSelect(combo.parentB)} />
            <div className="multi-target-result-list">
              {combo.targets.map((target) => (
                <button className="multi-target-hit" key={target.dragon.id} onClick={() => onRecipeTargetSelect(target.dragon)} type="button" title={`${target.dragon.name} 교배식 보기`}>
                  <DragonEgg dragon={target.dragon} size="xs" />
                  <b>{target.dragon.name}</b>
                  <ChanceBadge result={target} />
                </button>
              ))}
              {combo.missedTargets.map((dragon) => (
                <button className="multi-target-miss" key={dragon.id} onClick={() => onRecipeTargetSelect(dragon)} type="button" title={`${dragon.name} 교배식 보기`}>
                  {dragon.name}
                </button>
              ))}
            </div>
            <TotalChanceValue combo={combo} />
            <span>{formatDuration(combo.expectedSeconds)}</span>
            <span>
              {combo.hitCount}/{combo.totalTargets} · 기대 {formatAttempts(combo.totalChance)}
            </span>
          </article>
        ))}
      </div>

      {allComboCount > combos.length && (
        <p className="multi-result-note">
          필터 적용 전 {formatCountLabel(allComboCount, "combos")}이 있습니다.
        </p>
      )}
    </section>
  );
}

function DragonToken({ dragon, useEgg = false }: { dragon: Dragon; useEgg?: boolean }) {
  return (
    <span className="dv-dragon-token">
      {useEgg ? <DragonEgg dragon={dragon} size="sm" /> : <DragonArt dragon={dragon} size="sm" />}
      <b>{dragon.name}</b>
    </span>
  );
}

function DragonTokenButton({ dragon, onClick, useEgg = false }: { dragon: Dragon; onClick: () => void; useEgg?: boolean }) {
  return (
    <button className="dv-dragon-token-button" onClick={onClick} type="button" title={`${dragon.name} 교배식 보기`}>
      <DragonToken dragon={dragon} useEgg={useEgg} />
    </button>
  );
}

function hasChanceDelta(chance: number, baseChance: number) {
  return Math.abs(chance - baseChance) >= 0.005;
}

function ChanceValue({ result }: { result: BreedingResult }) {
  const changed = hasChanceDelta(result.chance, result.baseChance);

  return (
    <strong className={`chance-value ${result.isRateUp ? "has-event-rate" : ""}`}>
      <span className="chance-main">
        {result.chanceText}
        {result.isRateUp && <em>이벤트</em>}
      </span>
      {changed && <span className="chance-base">기본 {result.baseChanceText}</span>}
    </strong>
  );
}

function ChanceBadge({ result }: { result: BreedingResult }) {
  const changed = hasChanceDelta(result.chance, result.baseChance);

  return (
    <em className={`chance-badge ${result.isRateUp ? "has-event-rate" : ""}`}>
      {result.chanceText}
      {result.isRateUp && <span>이벤트</span>}
      {changed && <small>기본 {result.baseChanceText}</small>}
    </em>
  );
}

function TotalChanceValue({ combo }: { combo: MultiTargetCombo }) {
  const changed = hasChanceDelta(combo.totalChance, combo.baseTotalChance);
  const hasRateUpTarget = combo.targets.some((target) => target.isRateUp);

  return (
    <strong className={`chance-value ${hasRateUpTarget ? "has-event-rate" : ""}`}>
      <span className="chance-main">
        {combo.totalChanceText}
        {hasRateUpTarget && <em>이벤트</em>}
      </span>
      {changed && <span className="chance-base">기본 {combo.baseTotalChanceText}</span>}
    </strong>
  );
}

function TargetCalculator({
  targetId,
  target,
  dragons: selectDragons,
  onTargetChange
}: {
  targetId: number;
  target: Dragon;
  dragons: Dragon[];
  onTargetChange: (value: number) => void;
}) {
  return (
    <section className="calculator-card">
      <div className="calc-head">
        <div>
          <p className="eyebrow">목표로 보기</p>
          <h2>{target.name} 부모 조합</h2>
        </div>
      </div>
      <DragonShowcase dragon={target} />
      <DragonSelect label="목표 드래곤" value={targetId} onChange={onTargetChange} dragons={selectDragons} />
    </section>
  );
}

function ParentPairCalculator({
  parentAId,
  parentBId,
  parentA,
  parentB,
  dragons: selectDragons,
  onParentAChange,
  onParentBChange
}: {
  parentAId: number;
  parentBId: number;
  parentA: Dragon;
  parentB: Dragon;
  dragons: Dragon[];
  onParentAChange: (value: number) => void;
  onParentBChange: (value: number) => void;
}) {
  return (
    <section className="calculator-card">
      <div className="calc-head">
        <div>
          <p className="eyebrow">부모로 보기</p>
          <h2>
            {parentA.name} <Plus size={18} /> {parentB.name}
          </h2>
        </div>
      </div>
      <div className="parent-pair-grid">
        <DragonShowcase dragon={parentA} compact />
        <DragonSelect label="부모 1" value={parentAId} onChange={onParentAChange} dragons={selectDragons} />
        <DragonShowcase dragon={parentB} compact />
        <DragonSelect label="부모 2" value={parentBId} onChange={onParentBChange} dragons={selectDragons} />
      </div>
    </section>
  );
}

function ForwardPanel({
  parentA,
  parentB,
  results,
  allResultCount,
  warnings
}: {
  parentA: Dragon;
  parentB: Dragon;
  results: BreedingResult[];
  allResultCount: number;
  warnings: string[];
}) {
  return (
    <section className="result-panel">
      <div className="panel-heading">
        <div>
          <p className="eyebrow">결과 후보</p>
          <h2>
            {parentA.name} <Plus size={18} /> {parentB.name}
          </h2>
        </div>
        <strong>{formatCountLabel(allResultCount, "species")}</strong>
      </div>
      <p className="panel-subline">선택한 부모 조합에서 나올 수 있는 후보를 확률순으로 보여줍니다.</p>
      {warnings.length > 0 && (
        <div className="warning-row">
          {warnings.map((warning) => (
            <span key={warning}>{warning}</span>
          ))}
        </div>
      )}
      <ForwardPreview parentA={parentA} parentB={parentB} results={results} allResultCount={allResultCount} />
      <ResultTable results={results} />
    </section>
  );
}

function ReversePanel({ target, combos, allComboCount }: { target: Dragon; combos: ParentCombo[]; allComboCount: number }) {
  return (
    <section className="result-panel">
      <div className="panel-heading">
        <div>
          <p className="eyebrow">부모 조합</p>
          <h2>{target.name} 역교배</h2>
        </div>
        <strong>{formatCountLabel(allComboCount, "items")}</strong>
      </div>
      <p className="panel-subline">필터 조건에 맞는 상위 {formatCountLabel(Math.min(combos.length, 24), "combos")}을 먼저 보여줍니다.</p>
      <FeaturedRecipe target={target} combo={combos[0]} allComboCount={allComboCount} />
      <ComboList combos={combos} />
    </section>
  );
}

function FeaturedRecipe({
  target,
  combo,
  allComboCount
}: {
  target: Dragon;
  combo?: ParentCombo;
  allComboCount: number;
}) {
  if (!combo) {
    return (
      <section className="featured-recipe empty-feature">
        <DragonShowcase dragon={target} />
        <p>조건에 맞는 대표 조합이 없습니다.</p>
      </section>
    );
  }

  return (
    <section className="featured-recipe">
      <div className="featured-copy">
        <p className="eyebrow">추천 루트</p>
        <h3>{target.name} 대표 조합</h3>
        <div className="featured-stats">
          <span>{combo.target.chanceText}</span>
          <span>기대 {formatAttempts(combo.target.chance)}</span>
          <span>{formatDuration(combo.expectedSeconds)}</span>
          <span>{formatCountLabel(allComboCount, "combos")}</span>
        </div>
      </div>
      <div className="breeding-stage" aria-label={`${target.name} 대표 교배 조합`}>
        <StageDragon dragon={combo.parentA} label="부모 1" />
        <span className="stage-mark plus-mark">
          <Plus size={18} />
        </span>
        <StageDragon dragon={combo.parentB} label="부모 2" />
        <span className="stage-mark arrow-mark">
          <ArrowRight size={20} />
        </span>
        <StageOutcome dragon={target} chanceText={combo.target.chanceText} />
      </div>
    </section>
  );
}

function ForwardPreview({
  parentA,
  parentB,
  results,
  allResultCount
}: {
  parentA: Dragon;
  parentB: Dragon;
  results: BreedingResult[];
  allResultCount: number;
}) {
  const topResults = results.slice(0, 3);

  return (
    <section className="featured-recipe forward-feature">
      <div className="featured-copy">
        <p className="eyebrow">부화 미리보기</p>
        <h3>
          {parentA.name} + {parentB.name}
        </h3>
        <div className="featured-stats">
          <span>{formatCountLabel(allResultCount, "candidates")}</span>
          {topResults[0] ? <span>최고 {topResults[0].chanceText}</span> : <span>결과 없음</span>}
        </div>
      </div>
      <div className="breeding-stage forward-stage" aria-label="부모 조합 결과 미리보기">
        <StageDragon dragon={parentA} label="부모 1" />
        <span className="stage-mark plus-mark">
          <Plus size={18} />
        </span>
        <StageDragon dragon={parentB} label="부모 2" />
        <span className="stage-mark arrow-mark">
          <ArrowRight size={20} />
        </span>
        <div className="outcome-stack">
          {topResults.length > 0 ? (
            topResults.map((result) => (
              <StageOutcome key={result.dragon.id} dragon={result.dragon} chanceText={result.chanceText} compact />
            ))
          ) : (
            <p>조건에 맞는 결과가 없습니다.</p>
          )}
        </div>
      </div>
    </section>
  );
}

function StageDragon({ dragon, label }: { dragon: Dragon; label: string }) {
  return (
    <div className={`stage-dragon stage-${getPrimaryElementCode(dragon)}`}>
      <span>{label}</span>
      <DragonArt dragon={dragon} size="md" />
      <strong>{dragon.name}</strong>
    </div>
  );
}

function StageOutcome({
  dragon,
  chanceText,
  compact = false
}: {
  dragon: Dragon;
  chanceText: string;
  compact?: boolean;
}) {
  return (
    <div className={`stage-outcome ${compact ? "compact" : ""} stage-${getPrimaryElementCode(dragon)}`}>
      <DragonEgg dragon={dragon} size={compact ? "sm" : "lg"} />
      <div>
        <span>결과</span>
        <strong>{dragon.name}</strong>
        <small>{chanceText}</small>
      </div>
    </div>
  );
}

function ComboList({ combos }: { combos: ParentCombo[] }) {
  if (combos.length === 0) return <p className="empty">조건에 맞는 부모 조합이 없습니다.</p>;
  return (
    <div className="combo-list">
      {combos.slice(0, 24).map((combo, index) => (
        <article className="combo-card combo-row" key={`${combo.parentA.id}-${combo.parentB.id}-${combo.target.dragon.id}`}>
          <span className="rank-badge">{index + 1}</span>
          <div className="combo-title">
            <DragonMini dragon={combo.parentA} />
            <Plus size={16} />
            <DragonMini dragon={combo.parentB} />
          </div>
          <div className="combo-result">
            <span className="recipe-arrow">
              <ArrowRight size={17} />
            </span>
            <DragonEgg dragon={combo.target.dragon} size="sm" />
            <span>
              <b>{combo.target.dragon.name}</b>
            </span>
          </div>
          <div className="combo-score">
            <ChanceValue result={combo.target} />
            <span>기대 {formatAttempts(combo.target.chance)}</span>
          </div>
          <div className="combo-time">
            <Clock3 size={14} />
            {formatDuration(combo.expectedSeconds)}
          </div>
          <p>부산물: {combo.byproducts.map((item) => item.dragon.name).join(", ") || "없음"}</p>
        </article>
      ))}
    </div>
  );
}

function PathPanel({
  target,
  ownedDragons,
  ownedDids,
  setOwnedDids,
  pathPlan,
  onRecipeTargetSelect
}: {
  target: Dragon;
  ownedDragons: Dragon[];
  ownedDids: Set<number>;
  setOwnedDids: React.Dispatch<React.SetStateAction<Set<number>>>;
  pathPlan: ReturnType<typeof findShortestPath>;
  onRecipeTargetSelect: (dragon: Dragon) => void;
}) {
  const availableToAdd = dragons.filter((dragon) => !ownedDids.has(dragon.id));

  return (
    <section className="result-panel path-panel">
      <div className="panel-heading">
        <div>
          <p className="eyebrow">보유 기반 최단 경로</p>
          <h2>{target.name}까지</h2>
        </div>
        <strong>{formatCountLabel(ownedDragons.length, "owned")}</strong>
      </div>
      <div className="owned-tools">
        <DragonSelect
          label="보유 추가"
          value={availableToAdd[0]?.id ?? dragons[0].id}
          onChange={(did) => setOwnedDids((current) => new Set([...current, did]))}
          dragons={availableToAdd.length ? availableToAdd : dragons}
        />
        <button className="icon-button" title="상점 구매 가능 드래곤으로 초기화" onClick={() => setOwnedDids(new Set(initialOwned))}>
          <Filter size={18} />
          기본값
        </button>
      </div>
      <div className="chip-row">
        {ownedDragons.slice(0, 28).map((dragon) => (
          <button
            className="owned-chip"
            key={dragon.id}
            title="보유 목록에서 제거"
            onClick={() =>
              setOwnedDids((current) => {
                const next = new Set(current);
                next.delete(dragon.id);
                return next;
              })
            }
          >
            {dragon.name}
            <X size={13} />
          </button>
        ))}
      </div>
      <div className="path-list">
        {ownedDids.has(target.id) ? (
          <p className="empty">이미 보유 목록에 있습니다.</p>
        ) : pathPlan.reachable ? (
          pathPlan.steps.map((step, index) => (
            <article className="path-step" key={`${step.to.id}-${index}`}>
              <span>{index + 1}</span>
              <DragonMiniButton dragon={step.from[0]} onClick={() => onRecipeTargetSelect(step.from[0])} />
              <Plus size={15} />
              <DragonMiniButton dragon={step.from[1]} onClick={() => onRecipeTargetSelect(step.from[1])} />
              <button className="path-target-button" onClick={() => onRecipeTargetSelect(step.to)} type="button" title={`${step.to.name} 교배식 보기`}>
                {step.to.name}
              </button>
              <em>{step.chance.toFixed(2)}%</em>
            </article>
          ))
        ) : (
          <p className="empty">현재 보유 목록과 레벨 조건으로는 경로를 찾지 못했습니다.</p>
        )}
      </div>
    </section>
  );
}

function ResultTable({ results }: { results: BreedingResult[] }) {
  if (results.length === 0) return <p className="empty">조건에 맞는 결과가 없습니다.</p>;
  return (
    <div className="result-table">
      <div className="table-row table-head">
        <span>드래곤</span>
        <span>알</span>
        <span>확률</span>
        <span>방식</span>
        <span>시간</span>
        <span>기대</span>
      </div>
      {results.slice(0, 60).map((result) => (
        <div className="table-row" key={result.dragon.id}>
          <DragonMini dragon={result.dragon} />
          <DragonEgg dragon={result.dragon} size="xs" />
          <ChanceValue result={result} />
          <span>{result.mode === "fixed" ? "고정" : `가중치 ${result.rawWeight}`}</span>
          <span>
            <Clock3 size={14} />
            {formatDuration(result.breedingSeconds + result.hatchingSeconds)}
          </span>
          <span>{formatAttempts(result.chance)}</span>
        </div>
      ))}
    </div>
  );
}

function DragonSelect({
  label,
  value,
  onChange,
  dragons: selectDragons
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  dragons: Dragon[];
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(Number(event.target.value))}>
        {[...selectDragons]
          .sort((a, b) => compareLocalized(a.name, b.name))
          .map((dragon) => (
            <option key={dragon.id} value={dragon.id}>
              {dragon.name}
            </option>
          ))}
      </select>
    </label>
  );
}

function DragonMini({ dragon }: { dragon: Dragon }) {
  return (
    <span className={`dragon-mini dragon-mini-${getPrimaryElementCode(dragon)}`}>
      <DragonArt dragon={dragon} size="sm" />
      <span>
        <b>{dragon.name}</b>
      </span>
    </span>
  );
}

function DragonMiniButton({ dragon, onClick }: { dragon: Dragon; onClick: () => void }) {
  return (
    <button className="dragon-mini-button" onClick={onClick} type="button" title={`${dragon.name} 교배식 보기`}>
      <DragonMini dragon={dragon} />
    </button>
  );
}

function DragonShowcase({ dragon, compact = false }: { dragon: Dragon; compact?: boolean }) {
  return (
    <div className={`dragon-showcase ${compact ? "compact" : ""} showcase-${getPrimaryElementCode(dragon)}`}>
      <div className="showcase-art-stack">
        <DragonEgg dragon={dragon} size={compact ? "md" : "lg"} />
        <DragonArt dragon={dragon} size={compact ? "md" : "lg"} />
      </div>
      <div>
        <strong>{dragon.name}</strong>
      </div>
    </div>
  );
}

function DragonEgg({ dragon, size }: { dragon: Dragon; size: "xs" | "sm" | "md" | "lg" }) {
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => setImageFailed(false), [dragon.code]);
  const egg = imageFailed ? null : getEggPath(dragon);

  return (
    <span className={`egg-art ${size} ${egg ? "" : "fallback"}`}>
      {egg ? (
        <img src={egg} alt="" loading="lazy" decoding="async" onError={() => setImageFailed(true)} />
      ) : (
        <b>{dragon.name.slice(0, 1)}</b>
      )}
    </span>
  );
}

function DragonArt({ dragon, size }: { dragon: Dragon; size: "sm" | "md" | "lg" }) {
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => setImageFailed(false), [dragon.code]);
  const portrait = imageFailed ? null : getPortraitPath(dragon);

  return (
    <span className={`dragon-art ${size} rarity-ring-${dragon.rarity} ${portrait ? "" : "fallback"}`}>
      {portrait ? (
        <img src={portrait} alt="" loading="lazy" decoding="async" onError={() => setImageFailed(true)} />
      ) : (
        <b>{dragon.name.slice(0, 1)}</b>
      )}
    </span>
  );
}

function StatTile({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <article className="stat-tile">
      {icon}
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}

function filterBreedingResults(results: BreedingResult[], query: string, dropFilter: string, minChance: number) {
  return results.filter((result) => {
    const matchesQuery = normalize(`${result.dragon.name}${result.dragon.code}${result.dragon.elementNames.join("")}`).includes(
      normalize(query)
    );
    const matchesDrop = dropFilter === "all" || result.dragon.drop === dropFilter;
    return matchesQuery && matchesDrop && result.chance >= minChance;
  });
}

function filterParentCombos(combos: ParentCombo[], query: string, minChance: number) {
  return combos
    .filter((combo) => {
      const haystack = `${combo.parentA.name}${combo.parentB.name}${combo.parentA.code}${combo.parentB.code}`;
      return normalize(haystack).includes(normalize(query)) && combo.target.chance >= minChance;
    })
    .sort((a, b) => b.target.chance - a.target.chance || a.expectedSeconds - b.expectedSeconds);
}

function filterMultiTargetCombos(combos: MultiTargetCombo[], query: string, minChance: number) {
  const normalizedQuery = normalize(query);
  return combos
    .filter((combo) => {
      const haystack = normalize(
        `${combo.parentA.name}${combo.parentB.name}${combo.parentA.code}${combo.parentB.code}${combo.targets
          .map((target) => target.dragon.name + target.dragon.code)
          .join("")}`
      );
      return (!normalizedQuery || haystack.includes(normalizedQuery)) && combo.totalChance >= minChance;
    })
    .sort((a, b) => {
      const base = b.hitCount - a.hitCount;
      if (base !== 0) return base;
      return b.totalChance - a.totalChance || b.bestChance - a.bestChance || a.expectedSeconds - b.expectedSeconds;
    });
}

function filterDirectoryDragons(
  sourceDragons: Dragon[],
  query: string,
  dropFilter: string,
  elementFilter: string,
  sort: DirectorySort
) {
  const normalizedQuery = normalize(query);
  return sourceDragons
    .filter((dragon) => {
      const haystack = normalize(
        `${dragon.name}${dragon.code}${dragon.elementNames.join("")}${getDropLabel(dragon.drop)}${formatDragonText(dragon.ecology.obtain)}`
      );
      const matchesQuery = !normalizedQuery || haystack.includes(normalizedQuery);
      const matchesDrop = dropFilter === "all" || dragon.drop === dropFilter;
      const matchesElement = elementFilter === "all" || dragon.elementCodes.includes(elementFilter);
      return matchesQuery && matchesDrop && matchesElement;
    })
    .sort((a, b) => {
      if (sort === "name") return compareLocalized(a.name, b.name) || a.encyclopediaNo - b.encyclopediaNo;
      if (sort === "rarity") return b.rarity - a.rarity || a.encyclopediaNo - b.encyclopediaNo;
      return a.encyclopediaNo - b.encyclopediaNo || compareLocalized(a.name, b.name);
    });
}

function getFeaturedDragonItems(recentIds: number[]): FeaturedDragonItem[] {
  const dragonById = new Map(dragons.map((dragon) => [dragon.id, dragon]));
  const items: FeaturedDragonItem[] = [];
  const seen = new Set<number>();
  const push = (dragon: Dragon | undefined, reason: FeaturedDragonReason) => {
    if (!dragon || !hasBreedingRecipe(dragon) || seen.has(dragon.id)) return;
    seen.add(dragon.id);
    items.push({ dragon, reason });
  };

  activeEventRateUpDragons.forEach((dragon) => push(dragon, "event"));
  recentIds.forEach((did) => push(dragonById.get(did), "recent"));
  calculatorTargetDragons
    .slice()
    .sort((a, b) => b.rarity - a.rarity || a.encyclopediaNo - b.encyclopediaNo || compareLocalized(a.name, b.name))
    .forEach((dragon) => push(dragon, "recommended"));

  return items.slice(0, 6);
}

function getFeaturedReasonLabel(reason: FeaturedDragonReason) {
  if (reason === "event") return "이벤트";
  if (reason === "recent") return "최근";
  return "추천";
}

function getEventStatus(event: EventRateUpEntry) {
  const now = Date.now();
  if (event.disabled) return "disabled";
  if (event.startsAt > now) return "upcoming";
  if (event.endsAt < now) return "ended";
  return "active";
}

function getEventStatusLabel(status: ReturnType<typeof getEventStatus>) {
  if (status === "active") return "진행 중";
  if (status === "upcoming") return "예정";
  if (status === "disabled") return "비활성";
  return "종료";
}

function getDropLabel(drop: string) {
  return dropFilterOptions.find((option) => option.value === drop)?.label ?? drop;
}

function formatDragonText(value: string) {
  return value.replace(/\\n/g, "\n").trim();
}

function getDragonDetailPath(dragon: Dragon) {
  return `/dragons/${encodeURIComponent(dragon.code.toLowerCase())}`;
}

function getCalculatorTargetPath(dragon: Dragon) {
  return `/?target=${encodeURIComponent(dragon.code.toLowerCase())}`;
}

function getTargetIdFromUrl() {
  const raw = new URLSearchParams(window.location.search).get("target")?.trim();
  if (!raw) return null;

  const numeric = Number(raw);
  const normalized = normalize(raw);
  const target = dragons.find(
    (dragon) =>
      dragon.id === numeric ||
      dragon.code.toLowerCase() === raw.toLowerCase() ||
      normalize(dragon.name) === normalized
  );
  return target?.id ?? null;
}

function getDirectoryFiltersFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const query = params.get("q")?.trim() ?? "";
  const drop = params.get("drop") ?? "all";
  const element = params.get("element") ?? "all";

  return {
    query,
    drop: dropFilterOptions.some((option) => option.value === drop) ? drop : "all",
    element: element === "all" || elementFilterOptions.some((option) => option.code === element) ? element : "all"
  };
}

function getRelatedDragons(dragon: Dragon) {
  const primaryElement = getPrimaryElementCode(dragon);
  return dragons
    .filter((item) => item.id !== dragon.id && getPrimaryElementCode(item) === primaryElement)
    .sort((a, b) => b.rarity - a.rarity || a.encyclopediaNo - b.encyclopediaNo)
    .slice(0, 4);
}

function getDragonFaqItems(
  dragon: Dragon,
  targetCombos: ParentCombo[],
  parentOutcomes: ParentOutcomePreview[],
  obtainText: string
) {
  const bestCombo = targetCombos[0];
  return [
    {
      question: `${dragon.name}은 어떻게 얻나요?`,
      answer: bestCombo
        ? `${dragon.name}은 교배로 얻을 수 있습니다. 추천 조합은 ${bestCombo.parentA.name} + ${bestCombo.parentB.name}이며 성공 확률은 ${bestCombo.target.chanceText}, 부화 시간은 ${formatDuration(bestCombo.expectedSeconds)}입니다.`
        : `${dragon.name}은 현재 데이터 기준으로 교배 조합이 없으며, 획득처는 ${obtainText}입니다.`
    },
    {
      question: `${dragon.name} 교배식이 있나요?`,
      answer:
        targetCombos.length > 0
          ? `확인된 ${dragon.name} 교배식은 ${targetCombos.length}개입니다. 상세 페이지에는 확률이 높은 조합부터 먼저 표시됩니다.`
          : `${dragon.name}은 현재 교배식이 없으며, 계산기와 도감에서 획득처 정보를 기준으로 확인할 수 있습니다.`
    },
    {
      question: `${dragon.name}을 부모로 쓸 수 있나요?`,
      answer: dragon.canBreedAsParent
        ? `${dragon.name}은 부모로 사용할 수 있습니다. 대표 결과 ${parentOutcomes.length}개를 부모 활용 목록에서 확인할 수 있습니다.`
        : `${dragon.name}은 현재 데이터 기준으로 부모 교배에 사용할 수 없습니다.`
    }
  ];
}

function buildCalculatorJsonLd(target: Dragon) {
  return [
    {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "DV3 공략 교배 계산기",
      url: absoluteUrl(getCalculatorTargetPath(target)),
      applicationCategory: "GameApplication",
      operatingSystem: "Web",
      description: `${target.name}을 얻기 위한 드래곤빌리지3 교배 조합과 최단 루트를 계산합니다.`
    },
    buildBreadcrumbJsonLd([
      { name: "DV3 공략", path: "/" },
      { name: `${target.name} 교배 계산기`, path: getCalculatorTargetPath(target) }
    ])
  ];
}

function buildFaqJsonLd(items: Array<{ question: string; answer: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer
      }
    }))
  };
}

function buildStaticPageJsonLd(
  page: StaticPageId,
  content: { title: string; sections: Array<{ heading: string; body: string[] }> }
) {
  return [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: content.title,
      url: absoluteUrl(`/${page}`),
      description: content.sections[0]?.body[0] ?? "DV3 공략 사이트 정보 페이지입니다.",
      isPartOf: {
        "@id": `${siteOrigin}/#website`
      },
      mainEntity: content.sections.map((section) => ({
        "@type": "Thing",
        name: section.heading,
        description: section.body.join(" ")
      }))
    },
    buildBreadcrumbJsonLd([
      { name: "DV3 공략", path: "/" },
      { name: content.title, path: `/${page}` }
    ])
  ];
}

function buildEventsJsonLd(events: EventRateUpEntry[]) {
  return [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "드래곤빌리지3 이벤트 확률업",
      url: absoluteUrl("/events"),
      description: "드래곤빌리지3 교배 확률업 이벤트 대상, 기간, 추천 조합을 정리한 페이지입니다.",
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: events.length,
        itemListElement: events.flatMap((event, eventIndex) =>
          event.dragonNames.map((name, nameIndex) => ({
            "@type": "ListItem",
            position: eventIndex * 10 + nameIndex + 1,
            name,
            description: `${formatDateTime(event.startsAt, "ko")}부터 ${formatDateTime(event.endsAt, "ko")}까지 교배 확률업`
          }))
        )
      },
      isPartOf: {
        "@id": `${siteOrigin}/#website`
      }
    },
    buildBreadcrumbJsonLd([
      { name: "DV3 공략", path: "/" },
      { name: "이벤트 확률업", path: "/events" }
    ])
  ];
}

function buildDirectoryJsonLd() {
  return [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "드래곤빌리지3 도감",
      url: absoluteUrl("/dragons"),
      description: "드래곤빌리지3 드래곤의 속성, 획득처, 부화 시간, 희귀도 정보를 정리한 도감입니다.",
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: dragons.length,
        itemListElement: dragons.slice(0, 30).map((dragon, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: dragon.name,
          url: absoluteUrl(getDragonDetailPath(dragon))
        }))
      }
    },
    buildBreadcrumbJsonLd([
      { name: "DV3 공략", path: "/" },
      { name: "드래곤 도감", path: "/dragons" }
    ])
  ];
}

function buildDragonDetailJsonLd(dragon: Dragon, faqItems: Array<{ question: string; answer: string }> = []) {
  const imagePath = getPortraitPath(dragon) ?? getEggPath(dragon) ?? "/assets/dv3-tool-preview.png";
  const obtainText = getObtainText(dragon) ?? formatDragonText(dragon.ecology.obtain);
  const dragonEntity = {
    "@type": "Thing",
    name: dragon.name,
    alternateName: dragon.code,
    image: absoluteUrl(imagePath),
    description: formatDragonText(dragon.ecology.obtain || dragon.ecology.egg || `${dragon.name} 도감 정보`),
    additionalProperty: [
      { "@type": "PropertyValue", name: "속성", value: dragon.elementNames.join(", ") },
      { "@type": "PropertyValue", name: "획득처", value: obtainText },
      { "@type": "PropertyValue", name: "희귀도", value: String(dragon.rarity) },
      { "@type": "PropertyValue", name: "부화 시간", value: formatDuration(dragon.hatchingSeconds) },
      { "@type": "PropertyValue", name: "교배 시간", value: formatDuration(dragon.breedingSeconds) },
      { "@type": "PropertyValue", name: "부모 가능", value: dragon.canBreedAsParent ? "가능" : "불가" }
    ]
  };
  return [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: `${dragon.name} 교배식 · 도감`,
      url: absoluteUrl(getDragonDetailPath(dragon)),
      description: `${dragon.name}의 교배 조합, 획득처, 부화 시간, 속성, 능력치 정보를 정리했습니다.`,
      image: absoluteUrl(imagePath),
      about: dragonEntity,
      mainEntity: dragonEntity,
      isPartOf: {
        "@id": `${siteOrigin}/#website`
      }
    },
    ...(faqItems.length > 0 ? [buildFaqJsonLd(faqItems)] : []),
    buildBreadcrumbJsonLd([
      { name: "DV3 공략", path: "/" },
      { name: "드래곤 도감", path: "/dragons" },
      { name: dragon.name, path: getDragonDetailPath(dragon) }
    ])
  ];
}

function buildBreadcrumbJsonLd(items: Array<{ name: string; path: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path)
    }))
  };
}

function getDragonFormPath(dragon: Dragon, variant: DragonFormVariant, stage: DragonFormStage) {
  return `/assets/dragon_forms/${variant}/${dragon.code.toLowerCase()}_${stage}.webp`;
}

function getDragonChromaIconPath(dragon: Dragon, stage: DragonFormStage) {
  return `/assets/dragon_chroma_icons/${dragon.code.toLowerCase()}_${stage}.webp`;
}

function getOrderedFormStages(stages: DragonFormStage[] = []) {
  return formStageOrder.filter((stage) => stages.includes(stage));
}

function getDragonFromPath(pathname: string) {
  const slug = decodeURIComponent(pathname.replace(/^\/dragons\//, "")).toLowerCase();
  return dragons.find(
    (dragon) =>
      dragon.code.toLowerCase() === slug ||
      String(dragon.id) === slug ||
      normalize(dragon.name) === normalize(slug)
  );
}

function getStatMax(key: keyof Dragon["stats"]) {
  return Math.max(...dragons.map((dragon) => dragon.stats[key]));
}

function getParentRequirementText(dragon: Dragon) {
  const elementRequirements = dragon.breeding.parentElementRequirementNames;
  const dragonRequirements = dragon.breeding.parentDidRequirements
    .map((did) => dragons.find((item) => item.id === did)?.name)
    .filter((name): name is string => Boolean(name));
  const parts = [
    elementRequirements.length > 0 ? `${elementRequirements.join(", ")} 속성 부모` : "",
    dragonRequirements.length > 0 ? `${dragonRequirements.join(", ")} 필요` : ""
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
}

function getParentOutcomePreview(parent: Dragon, options: BreedingOptions): ParentOutcomePreview[] {
  if (!parent.canBreedAsParent) return [];
  const bestByResult = new Map<number, ParentOutcomePreview>();

  for (const partner of dragons) {
    if (!partner.canBreedAsParent || partner.id === parent.id) continue;
    const results = calculateBreedingResults(dragons, parent, partner, data.tierFixedRateMax, options);
    for (const result of results) {
      const current = bestByResult.get(result.dragon.id);
      if (!current || result.chance > current.result.chance) {
        bestByResult.set(result.dragon.id, { partner, result });
      }
    }
  }

  return [...bestByResult.values()]
    .sort((a, b) => b.result.chance - a.result.chance || a.result.hatchingSeconds - b.result.hatchingSeconds)
    .slice(0, 8);
}

function normalize(value: string) {
  return normalizeBasic(localizeForSearch(value));
}

function normalizeBasic(value: string) {
  return value.toLowerCase().replace(/\s/g, "");
}

function compareLocalized(a: string, b: string) {
  return translateText(a).localeCompare(translateText(b), activeLocaleCode);
}

function getPortraitPath(dragon: Dragon) {
  const direct = dragon.code.toLowerCase();
  return dragonAssetCodes.has(direct) ? `/assets/dragon_portraits_ui/${direct}.webp` : null;
}

function getEggPath(dragon: Dragon) {
  const direct = dragon.code.toLowerCase();
  return dragonAssetCodes.has(direct) ? `/assets/dragon_eggs_ui/${direct}.webp` : null;
}

function getPrimaryElementCode(dragon: Dragon) {
  return dragon.elementCodes.find((element) => eggElementCodes.has(element)) ?? "default";
}

const rootElement = document.getElementById("root")! as HTMLElement & { dv3Root?: ReturnType<typeof createRoot> };
const root = rootElement.dv3Root ?? createRoot(rootElement);
rootElement.dv3Root = root;
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
